import './style.css';
import { PHANTOMX_ASSETS, ACTUATOR_NAMES } from './phantomx';
import { DEFAULT_THEME } from './themes';
import type { FrameSnapshot } from './types';
import { MuJoCoRunner } from './mujoco-runner';
import { PhantomXViewer } from './phantomx-viewer';
import { getPublicAssetPath } from './public-path';
import { SMEControllerClient } from './sme-controller';
import type { WeightSet } from './sme-controller';
import { TrainingView, TRAINING_PARAMETER_DEFS } from './training-view';
import type { TrainingParameterValues } from './training-view';

type NumericRange = { min: number; max: number };
type SignalChartOptions = {
  range?: NumericRange;
  clampToRange?: boolean;
};

const CONTROL_DT = 0.05;
const BASIS_SNAPSHOT_OFFSET = 29;
const SNAPSHOT_WINDOW_LENGTH = 56;
const SNAPSHOT_CAPTURE_LENGTH = BASIS_SNAPSHOT_OFFSET + SNAPSHOT_WINDOW_LENGTH;
const WEIGHT_SLIDER_MIN = -2;
const WEIGHT_SLIDER_MAX = 2;
const WEIGHT_SLIDER_STEP = 0.01;
const DEFAULT_ACTION_PLOT_RANGE: NumericRange = { min: -1, max: 1 };
const SPEEDRUN_DEFAULT_SPEED = 4;
type TrainingActionMode = 'theta' | 'perturbed';
type TrainingParamTab = 'history' | 'new';
const TRAINING_HISTORY_DIR = getPublicAssetPath('training-history');
const TRAINING_HISTORY_MANIFEST_PATH = `${TRAINING_HISTORY_DIR}/manifest.json`;
const TRAINING_HISTORY_FORMAT_VERSION = 1;
const TRAINING_HISTORY_LABEL_PREFIX = 'SME-AGOL';
const ZERO_WEIGHT_OPTION_ID = '__zero_weight_option__';
const ZERO_WEIGHT_OPTION_LABEL = 'Zero weights';


declare global {
  interface Window {
    __SME_API_BASE__?: string;
  }
}

const API_BASE = (typeof window !== 'undefined' && window.__SME_API_BASE__) ?? (import.meta.env?.VITE_SME_API_BASE ?? 'http://localhost:8000');

function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  if (input.startsWith('http')) {
    return fetch(input, init);
  }
  return fetch(`${API_BASE}${input}`, init);
}


type ViewName = 'playground' | 'training' | 'testing';

interface EpisodeProgressDto {
  episode: number;
  total_episodes: number;
  actions: (number | null)[][];
  theta_actions?: (number | null)[][];
  theta_weights?: number[];
  history?: EpisodeProgressDto[];
  reward?: string | null;
  total_return?: number | null;
}

interface EpisodeResult {
  episode: number;
  totalEpisodes: number;
  actions: number[][];
  thetaActions: number[][] | null;
  thetaWeights: number[] | null;
  reward?: string | null;
  totalReturn?: number | null;
}

interface TrainingWeightExportPayload {
  exported_at: string;
  episode: number;
  total_episodes: number;
  theta_weights: number[];
  reward?: string | null;
  total_return?: number | null;
  parameters: TrainingParameterValues | null;
}

interface TrainingWeightRunEpisode {
  episode: number;
  total_episodes: number;
  theta_weights: number[];
  reward?: string | null;
  total_return?: number | null;
}

interface TrainingWeightRunExportPayload {
  exported_at: string;
  total_episodes: number;
  parameters: TrainingParameterValues | null;
  episodes: TrainingWeightRunEpisode[];
}

interface TrainingHistoryManifestEntry {
  id?: string;
  file: string;
  label?: string;
}

interface TrainingHistoryManifestPayload {
  runs: TrainingHistoryManifestEntry[];
}

interface TrainingHistoryJobMetadata {
  id: string;
  label: string;
  started_at: string;
  completed_at: string;
  duration_ms: number;
  algo?: string;
  env_xml?: string | null;
}

interface TrainingHistorySummary {
  episodes_recorded: number;
  best_return: number | null;
  average_return: number | null;
  final_return: number | null;
}

interface TrainingHistoryEpisode extends TrainingWeightRunEpisode {
  recorded_at?: string;
}

interface TrainingHistoryExportPayload {
  format_version: number;
  job: TrainingHistoryJobMetadata;
  parameters: TrainingParameterValues | null;
  metadata: Record<string, unknown>;
  summary: TrainingHistorySummary;
  episodes: TrainingHistoryEpisode[];
}



const DEFAULT_WEIGHT_TITLE = 'Select a joint';
const DEFAULT_STEP_LIMIT = 1000;
const DEFAULT_JOINT_CLAMP_RANGE: NumericRange = { min: -0.3, max: 0.3 };

const DEFAULT_BASIS_SNAPSHOT_WINDOW: number[][] = [
  [
    0.005832763283991061,
    0.00943064041322585,
    0.01895195538944508,
    0.06129023539764153,
    0.10821666496221813,
    0.15276828167730566,
    0.1950736754298027,
    0.23523831233453513,
    0.2733590317266511,
    0.30952370071237517,
    0.34380949317965104,
    0.3762792862743866,
    0.40697361502652574,
    0.43589020935478207,
    0.46292018950511704,
    0.48757244738355765,
    0.5069296781380708,
    0.5031860515650574,
    0.4530169783735553,
    0.40534597493313296,
    0.36004884368612,
    0.3170031396381621,
    0.27609091699112714,
    0.23719793651303028,
    0.20021228239790953,
    0.1650219365999655,
    0.13150978971638005,
    0.09954122436054244,
    0.06892454221121179,
    0.03922891591644351,
    0.008299708442181142,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0.00005790667669846354,
    0.00014358908751854722,
    0.00026625211833595516,
    0.00044070699176167795,
    0.0006875626054441134,
    0.00103598595029958,
    0.0015287956962394644,
    0.0022326334611348217,
    0.00326098513560946,
    0.004835999900471919
  ],
  [
    0,
    0,
    0,
    0,
    0.00001811625505960004,
    0.00009221543495036087,
    0.00019984979920114785,
    0.0003530146109831882,
    0.0005698020572705574,
    0.0008756466343330753,
    0.001307288672230796,
    0.001920468139895626,
    0.0028064559937985376,
    0.004133333230887323,
    0.006273681262515436,
    0.01035439401485867,
    0.02232203502534955,
    0.06913114863536338,
    0.11565504981315539,
    0.15983190131797126,
    0.20178050986430207,
    0.2416047653367245,
    0.279400107166011,
    0.3152528070705171,
    0.3492380224687872,
    0.3814157140918307,
    0.41182137307288247,
    0.44044177946852714,
    0.4671360978975493,
    0.49126992112264617,
    0.508706258132591,
    0.4846914577038848,
    0.43544359089886614,
    0.38865012874764115,
    0.3441863474195262,
    0.30193175310562614,
    0.26177027918477735,
    0.22358936208630648,
    0.18727846896713507,
    0.15272647123695282,
    0.11981611952271332,
    0.08840991346219956,
    0.05830334206794219,
    0.02899658739253531,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0
  ],
  [
    0.07193002487465125,
    0.04236312815716852,
    0.012754463549694846,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0.00003908262790806943,
    0.0001178220012371068,
    0.00023138066552122547,
    0.00039304269009809035,
    0.0006219299740350191,
    0.0009449854952085467,
    0.0014013828468069345,
    0.002051171785607268,
    0.002994255600949193,
    0.004419205726756196,
    0.006761064784454866,
    0.011431163885316717,
    0.026795268997492152,
    0.07508317281109828,
    0.12130242492627219,
    0.16519463737097917,
    0.20687220465649717,
    0.24643781706212697,
    0.28398582490703034,
    0.31960124442222815,
    0.3533576193521037,
    0.38531252243241987,
    0.4154971916980709,
    0.4438888251190427,
    0.47031717332780776,
    0.49400511490218796,
    0.5095131840359526,
    0.4637854800657345,
    0.415581738646641,
    0.3697805341713893,
    0.3262590881279505,
    0.28489925392328425,
    0.24558716062996122,
    0.208212238243586,
    0.17266567535397512,
    0.13883762421925003,
    0.10661121497702528,
    0.07584691155085646
  ],
  [
    0.4585874679276044,
    0.48370935348041044,
    0.5045120328135014,
    0.5066856220466333,
    0.4563468127647074,
    0.40851073889623707,
    0.3630571696594379,
    0.3198635663435162,
    0.27881184553665844,
    0.23978776966204046,
    0.20267967416413998,
    0.16737627839588018,
    0.13376230585199989,
    0.10170794618571884,
    0.07103678880274558,
    0.04138860864750918,
    0.011208559115056138,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0.000053177083919581315,
    0.00013573818191450895,
    0.00025419026634679613,
    0.00042280028408378523,
    0.0006615222698552112,
    0.0009985296753091334,
    0.0014750038344700702,
    0.002154605578920122,
    0.0031446231229213923,
    0.004651685217851379,
    0.0071684475816154725,
    0.01238938162055775,
    0.031251776447952774,
    0.07962658930565161,
    0.12561628743475237,
    0.169291118826084,
    0.21076154742876174,
    0.2501294504488107,
    0.2874883363109102,
    0.322922234711955,
    0.3565034036244884,
    0.38828747800757,
    0.4183021636654249,
    0.4465164628002648
  ],
];

function formatJointLabel(row: number): string {
  const raw = ACTUATOR_NAMES[row];
  if (!raw) {
    return `Row ${row + 1}`;
  }
  const withoutPrefix = raw.replace(/^j_/i, '').replace(/_ctrl$/i, '');
  return withoutPrefix.replace(/_/g, ' ').toUpperCase();
}

let controllerClient: SMEControllerClient | null = null;
let runner: MuJoCoRunner | null = null;
let viewer: PhantomXViewer | null = null;
let unsubscribeRunner: (() => void) | null = null;
let isRunning = false;
let weightSet: WeightSet | null = null;
let selectedWeightRow = -1;
let isBaseLifted = false;
let latestSnapshotStep = 0;
let stepLimit = DEFAULT_STEP_LIMIT;
let nextStepLimitTarget: number | null = null;
let jointClampRange: NumericRange = { ...DEFAULT_JOINT_CLAMP_RANGE };
let currentView: ViewName = 'playground';
const weightRowElements: HTMLElement[] = [];
const weightCellButtons: HTMLButtonElement[][] = [];
const SIGNAL_WINDOW_SECONDS = 4;
const SIGNAL_HISTORY_LENGTH = Math.max(10, Math.round(SIGNAL_WINDOW_SECONDS / CONTROL_DT));
const MAX_SIGNAL_CHANNELS = 8;
const SIGNAL_COLORS = ['#10b981', '#84cc16', '#f59e0b', '#0ea5e9', '#d946ef', '#14b8a6', '#ec4899', '#0f172a'];
const cpgHistory: number[][] = [];
const basisHistory: number[][] = [];
const basisSnapshots: number[][] = [];

const statusNode = document.querySelector('[data-status]');
const simTimeNode = document.querySelector('[data-simtime]');
const stepNode = document.querySelector('[data-step]');
const baseNode = document.querySelector('[data-base-height]');
const baseXNode = document.querySelector('[data-base-x]');
const baseYNode = document.querySelector('[data-base-y]');
const clampNode = document.querySelector('[data-clamp]');
const cpgCanvas = document.getElementById('cpg-canvas') as HTMLCanvasElement | null;
const basisCanvas = document.getElementById('basis-canvas') as HTMLCanvasElement | null;
const cpgLegend = document.querySelector('[data-cpg-legend]');
const basisLegend = document.querySelector('[data-basis-legend]');
const weightMatrix = document.querySelector('[data-weight-matrix]') as HTMLElement | null;
const weightInspectorHost = document.querySelector('[data-weight-inspector-host]') as HTMLElement | null;
const weightPanel = document.querySelector('.panel--weights');
const weightInspector = document.querySelector('[data-weight-inspector]') as HTMLElement | null;
const weightTitle = document.querySelector('[data-weight-title]') as HTMLElement | null;
const weightSliders = document.querySelector('[data-weight-sliders]') as HTMLElement | null;
const weightBasisCanvas = document.getElementById('weight-basis-canvas') as HTMLCanvasElement | null;
const weightBasisLegend = document.querySelector('[data-weight-basis-legend]');
const weightActionCanvas = document.getElementById('weight-action-canvas') as HTMLCanvasElement | null;
const weightCloseButton = document.querySelector('[data-weight-close]') as HTMLButtonElement | null;
const tabButtons = Array.from(document.querySelectorAll('[data-tab]')) as HTMLButtonElement[];
const viewSections = Array.from(document.querySelectorAll('[data-view]')) as HTMLElement[];
const trainingGrid = document.querySelector('[data-training-grid]') as HTMLElement | null;
const trainingParamsForm = document.querySelector('[data-training-form]') as HTMLElement | null;
const trainingStartButton = document.querySelector('[data-training-start]') as HTMLButtonElement | null;
const trainingStatusMessage = document.querySelector('[data-training-status-message]');
const trainingElapsedNode = document.querySelector('[data-training-elapsed]') as HTMLElement | null;
const trainingParamTabButtons = Array.from(
  document.querySelectorAll<HTMLButtonElement>('[data-training-tab]'),
);
const trainingParamPanes = Array.from(
  document.querySelectorAll<HTMLElement>('[data-training-pane]'),
);
const trainingHistorySelect = document.querySelector('[data-training-history-select]') as HTMLSelectElement | null;
const trainingHistoryEmpty = document.querySelector('[data-training-history-empty]') as HTMLElement | null;
const trainingHistoryParams = document.querySelector('[data-training-history-params]') as HTMLElement | null;
const trainingHistoryEmptyDefaultText = trainingHistoryEmpty?.textContent ?? '';
const viewerParamsContainer = document.querySelector('[data-viewer-params]') as HTMLElement | null;
const viewerParamsList = document.querySelector('[data-viewer-params-list]') as HTMLElement | null;
const viewerParamsEmpty = document.querySelector('[data-viewer-params-empty]') as HTMLElement | null;
const viewerParamsEmptyDefaultText = viewerParamsEmpty?.textContent ?? '';
const viewerHistorySelect = document.querySelector('[data-viewer-history-select]') as HTMLSelectElement | null;
const episodeNav = document.querySelector('[data-episode-nav]') as HTMLElement | null;
const episodeInput = document.querySelector('[data-episode-input]') as HTMLInputElement | null;
const episodeTotalLabel = document.querySelector('[data-episode-total]');
const episodePrevButton = document.querySelector('[data-episode-prev]') as HTMLButtonElement | null;
const episodeNextButton = document.querySelector('[data-episode-next]') as HTMLButtonElement | null;
const actionToggle = document.querySelector('[data-action-toggle]') as HTMLElement | null;
const actionModeButtons = Array.from(
  document.querySelectorAll<HTMLButtonElement>('[data-action-mode]'),
);
const trainingWeightsPanel = document.querySelector('[data-training-weights-panel]') as HTMLElement | null;
const trainingWeightsContainer = document.querySelector('[data-training-weights]') as HTMLElement | null;
const trainingWeightsEmpty = document.querySelector('[data-training-weights-empty]') as HTMLElement | null;
const trainingRunCurrentButton = document.querySelector('[data-training-run-current]') as HTMLButtonElement | null;
const trainingRunFinalButton = document.querySelector('[data-training-run-final]') as HTMLButtonElement | null;
const trainingSaveButton = document.querySelector('[data-training-save-weights]') as HTMLButtonElement | null;
const trainingSaveAllButton = document.querySelector('[data-training-save-all-weights]') as HTMLButtonElement | null;
const speedrunButton = document.querySelector('[data-speedrun-toggle]') as HTMLButtonElement | null;
const speedrunSlider = document.querySelector('[data-speedrun-speed]') as HTMLInputElement | null;
const speedrunSpeedLabel = document.querySelector('[data-speedrun-speed-label]');
let trainingView: TrainingView | null = null;
let trainingParameters: TrainingParameterValues | null = null;
let lastRunTrainingParameters: TrainingParameterValues | null = null;
let trainingEpisodes: EpisodeResult[] = [];
let currentEpisodeIndex = -1;
let activeTrainingJobId: string | null = null;
let trainingPollHandle: number | null = null;
let trainingElapsedHandle: number | null = null;
let trainingStartTimestamp = 0;
let speedrunHandle: number | null = null;
let isSpeedrunning = false;
let trainingActionMode: TrainingActionMode = 'theta';
let actionPlotRange: NumericRange = { ...DEFAULT_ACTION_PLOT_RANGE };
interface TrainingRunSnapshot {
  id: string;
  label: string;
  timestamp: number;
  parameters: TrainingParameterValues | null;
  history?: TrainingHistoryExportPayload | null;
  orderIndex?: number;
}
interface ActiveTrainingRunMetadata {
  id: string;
  label: string;
  startedAt: string;
  parameters: TrainingParameterValues | null;
}
let trainingParamsTab: TrainingParamTab = 'history';
let trainingRuns: TrainingRunSnapshot[] = [];
let activeTrainingRun: ActiveTrainingRunMetadata | null = null;
let selectedTrainingRunId: string | null = null;
let preferZeroWeightSelection = false;
let lastCompletedHistoryPayload: TrainingHistoryExportPayload | null = null;
let viewingHistoryParameters: TrainingParameterValues | null = null;
let viewingHistoryClampRange: NumericRange | null = null;
let viewingHistoryRunId: string | null = null;
let isViewingHistory = false;
let cachedLiveTrainingEpisodes: EpisodeResult[] = [];
let cachedLiveEpisodeIndex = -1;
let cachedLiveBasisSnapshots: number[][] = [];
let pendingViewerPlaybackRunId: string | null = null;
let isZeroWeightsSelectionActive = false;
let trainingHistoryLoadPromise: Promise<void> | null = null;
let stepLimitEnabled = true;
let userStepLimitPreference: boolean | null = null;
let isSyncingStepLimitToggle = false;
let lastStepLimitDefaultSelectionId: string | null = null;

const startButton = document.getElementById('btn-start');
const resetButton = document.getElementById('btn-reset');
const baseToggleButton = document.getElementById('btn-toggle-base');
const stepLimitInput = document.getElementById('step-limit-input') as HTMLInputElement | null;
const stepLimitToggle = document.getElementById('step-limit-toggle') as HTMLInputElement | null;
const clampToggle = document.getElementById('clamp-toggle') as HTMLInputElement | null;
const clampRangeMinInput = document.getElementById('clamp-range-min') as HTMLInputElement | null;
const clampRangeMaxInput = document.getElementById('clamp-range-max') as HTMLInputElement | null;
const speedSlider = document.getElementById('speed-slider') as HTMLInputElement | null;
const speedLabel = document.getElementById('speed-label');

if (stepLimitInput) {
  stepLimitInput.value = DEFAULT_STEP_LIMIT.toString();
}
stepLimitEnabled = stepLimitToggle ? Boolean(stepLimitToggle.checked) : true;
setClampRangeInputValues(jointClampRange);

weightCloseButton?.addEventListener('click', () => {
  closeWeightInspector();
});

stepLimitToggle?.addEventListener('change', () => {
  if (isSyncingStepLimitToggle) {
    return;
  }
  const enabled = Boolean(stepLimitToggle.checked);
  userStepLimitPreference = enabled;
  updateStepLimitEnabled(enabled, false);
});

function setStatus(message: string): void {
  if (statusNode) {
    statusNode.textContent = message;
  }
}

function updateBaseToggleLabel(): void {
  if (baseToggleButton) {
    baseToggleButton.textContent = isBaseLifted ? 'Drop base' : 'Lift base';
  }
}

function handleStepLimitInput(): void {
  if (!stepLimitInput) {
    return;
  }
  const parsed = Number.parseInt(stepLimitInput.value, 10);
  const resolved = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_STEP_LIMIT;
  stepLimit = resolved;
  stepLimitInput.value = `${resolved}`;
  if (isRunning && stepLimitEnabled) {
    scheduleStepLimitPause();
  } else if (!stepLimitEnabled) {
    clearStepLimitPause();
  }
}

function updateStepLimitEnabled(enabled: boolean, syncToggle = true): void {
  stepLimitEnabled = enabled;
  if (syncToggle && stepLimitToggle && stepLimitToggle.checked !== enabled) {
    isSyncingStepLimitToggle = true;
    stepLimitToggle.checked = enabled;
    isSyncingStepLimitToggle = false;
  }
  if (enabled) {
    if (isRunning) {
      scheduleStepLimitPause();
    } else {
      clearStepLimitPause();
    }
  } else {
    clearStepLimitPause();
  }
}

function scheduleStepLimitPause(): void {
  if (!stepLimitEnabled || !Number.isFinite(stepLimit) || stepLimit <= 0) {
    nextStepLimitTarget = null;
    return;
  }
  nextStepLimitTarget = latestSnapshotStep + stepLimit;
}

function clearStepLimitPause(): void {
  nextStepLimitTarget = null;
}

function applyStepLimitDefaultsForSelection(
  isZeroSelection: boolean,
  selectionId: string | null,
  force = false,
): void {
  if (!force && lastStepLimitDefaultSelectionId === selectionId) {
    return;
  }
  lastStepLimitDefaultSelectionId = selectionId;
  if (isZeroSelection) {
    updateStepLimitEnabled(false);
    return;
  }
  const preferred = userStepLimitPreference;
  if (preferred === null) {
    updateStepLimitEnabled(true);
  } else {
    updateStepLimitEnabled(preferred);
  }
}

function enforceStepLimit(step: number): void {
  if (!isRunning || nextStepLimitTarget === null) {
    return;
  }
  if (step >= nextStepLimitTarget) {
    clearStepLimitPause();
    pauseSimulation('Step limit reached; simulation paused');
  }
}

function setClampRangeInputValues(range: NumericRange): void {
  if (clampRangeMinInput) {
    clampRangeMinInput.value = `${range.min}`;
  }
  if (clampRangeMaxInput) {
    clampRangeMaxInput.value = `${range.max}`;
  }
}

function parseClampRangeInput(): NumericRange | null {
  if (!clampRangeMinInput || !clampRangeMaxInput) {
    return null;
  }
  const min = Number(clampRangeMinInput.value);
  const max = Number(clampRangeMaxInput.value);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
    return null;
  }
  return { min, max };
}

function applyJointClampRange(): void {
  if (!runner) {
    return;
  }
  const shouldClamp = Boolean(clampToggle?.checked);
  runner.setJointClampRange(shouldClamp ? jointClampRange : null);
}

function handleClampRangeInput(): void {
  const parsed = parseClampRangeInput();
  if (!parsed) {
    setClampRangeInputValues(jointClampRange);
    setStatus('Joint range requires finite min/max values with min < max.');
    return;
  }
  jointClampRange = parsed;
  applyJointClampRange();
}

function setupTabs(): void {
  tabButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const target = button.dataset.tab as ViewName | undefined;
      if (!target || target === currentView || button.disabled) {
        return;
      }
      showView(target);
    });
  });
  if (!trainingView) {
    trainingView = new TrainingView({
      gridElement: trainingGrid,
      paramsElement: trainingParamsForm,
      actuatorNames: ACTUATOR_NAMES,
      getActionSeries: (jointIndex) => computeActionSeries(jointIndex),
      renderSignalChart,
      clearCanvas,
      actionRange: DEFAULT_ACTION_PLOT_RANGE,
      onParametersChange: (values) => {
        trainingParameters = values;
        syncActionPlotRange();
      },
    });
    trainingView.initialize();
    trainingParameters = trainingView.getParameters();
    syncActionPlotRange();
  }
  showView('playground');
}

function setupTrainingControls(): void {
  trainingStartButton?.addEventListener('click', () => {
    void handleStartTraining();
  });
  episodePrevButton?.addEventListener('click', () => {
    const targetIndex = getWrappedEpisodeIndex(-1);
    if (typeof targetIndex === 'number') {
      showEpisode(targetIndex);
    }
  });
  episodeNextButton?.addEventListener('click', () => {
    const targetIndex = getWrappedEpisodeIndex(1);
    if (typeof targetIndex === 'number') {
      showEpisode(targetIndex);
    }
  });
  actionModeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const mode = button.dataset.actionMode as TrainingActionMode | undefined;
      if (mode) {
        setTrainingActionMode(mode);
      }
    });
  });
  trainingRunCurrentButton?.addEventListener('click', () => {
    handleRunCurrentEpisodeWeights();
  });
  trainingRunFinalButton?.addEventListener('click', () => {
    handleRunFinalEpisodeWeights();
  });
  trainingSaveButton?.addEventListener('click', () => {
    handleSaveWeights();
  });
  trainingSaveAllButton?.addEventListener('click', () => {
    handleSaveAllWeights();
  });
  episodeInput?.addEventListener('change', () => {
    handleEpisodeInputCommit();
  });
  episodeInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleEpisodeInputCommit();
    }
  });
  episodeInput?.addEventListener('focus', () => {
    stopSpeedrun();
    episodeInput.select();
  });
  speedrunButton?.addEventListener('click', () => {
    if (isSpeedrunning) {
      stopSpeedrun();
    } else {
      startSpeedrun();
    }
  });
  speedrunSlider?.addEventListener('input', () => {
    updateSpeedrunSpeedLabel();
    restartSpeedrunTimer();
  });
  updateSpeedrunSpeedLabel();
  updateTrainingActionToggle();
  updateEpisodeNav();
  renderTrainingWeights(null);
  updateTrainingWeightButtons();
  setupTrainingParameterTabs();
  setupViewerHistoryControls();
  refreshTrainingHistoryUI();
}


function setupTrainingParameterTabs(): void {
  trainingParamTabButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const target = button.dataset.trainingTab as TrainingParamTab | undefined;
      if (target) {
        setTrainingParamsTab(target);
      }
    });
  });
  trainingHistorySelect?.addEventListener('change', (event) => {
    const select = event.currentTarget as HTMLSelectElement;
    handleTrainingHistorySelectionChange(select.value, false);
  });
  updateTrainingParamTabs();
}

function setTrainingParamsTab(tab: TrainingParamTab): void {
  if (trainingParamsTab === tab) {
    if (tab === 'history') {
      applySelectedHistoryRun();
    } else {
      clearHistoryView();
    }
    return;
  }
  trainingParamsTab = tab;
  updateTrainingParamTabs();
  if (tab === 'history') {
    applySelectedHistoryRun();
  } else {
    clearHistoryView();
  }
}

function setupViewerHistoryControls(): void {
  viewerHistorySelect?.addEventListener('change', (event) => {
    const select = event.currentTarget as HTMLSelectElement;
    handleTrainingHistorySelectionChange(select.value, true);
  });
}

function updateTrainingParamTabs(): void {
  trainingParamTabButtons.forEach((button) => {
    const isActive = button.dataset.trainingTab === trainingParamsTab;
    button.classList.toggle('training-tabs__button--active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
  trainingParamPanes.forEach((pane) => {
    const isActive = pane.dataset.trainingPane === trainingParamsTab;
    pane.classList.toggle('training-pane--active', isActive);
    pane.hidden = !isActive;
  });
}

function refreshTrainingHistoryUI(): void {
  ensureSelectedTrainingRunId();
  updateTrainingHistorySelect();
  updateViewerHistorySelect();
  renderTrainingHistoryParams();
  applyStepLimitDefaultsForSelection(selectedTrainingRunId === ZERO_WEIGHT_OPTION_ID, selectedTrainingRunId);
}

function updateTrainingHistorySelect(): void {
  if (!trainingHistorySelect) {
    return;
  }
  trainingHistorySelect.innerHTML = '';
  if (trainingRuns.length === 0) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = 'No training runs yet';
    trainingHistorySelect.appendChild(option);
    trainingHistorySelect.disabled = true;
    trainingHistorySelect.value = '';
    trainingHistorySelect.selectedIndex = 0;
    return;
  }
  trainingHistorySelect.disabled = false;
  trainingRuns.forEach((run) => {
    const option = document.createElement('option');
    option.value = run.id;
    option.textContent = run.label;
    trainingHistorySelect.appendChild(option);
  });
  syncTrainingHistorySelectValues();
}

function updateViewerHistorySelect(): void {
  if (!viewerHistorySelect) {
    return;
  }
  viewerHistorySelect.innerHTML = '';
  appendZeroWeightOption(viewerHistorySelect);
  if (trainingRuns.length === 0) {
    appendEmptyHistoryOption(viewerHistorySelect);
  } else {
    trainingRuns.forEach((run) => {
      const option = document.createElement('option');
      option.value = run.id;
      option.textContent = run.label;
      viewerHistorySelect.appendChild(option);
    });
  }
  viewerHistorySelect.disabled = false;
  const value = resolveTrainingHistorySelection(selectedTrainingRunId);
  viewerHistorySelect.value = value;
}

function appendZeroWeightOption(select: HTMLSelectElement): void {
  const option = document.createElement('option');
  option.value = ZERO_WEIGHT_OPTION_ID;
  option.textContent = ZERO_WEIGHT_OPTION_LABEL;
  select.appendChild(option);
}

function appendEmptyHistoryOption(select: HTMLSelectElement): void {
  const option = document.createElement('option');
  option.value = '';
  option.textContent = 'No training runs yet';
  option.disabled = true;
  select.appendChild(option);
}

function resolveTrainingHistorySelection(selection: string | null): string {
  if (!selection) {
    return trainingRuns[0]?.id ?? ZERO_WEIGHT_OPTION_ID;
  }
  if (selection === ZERO_WEIGHT_OPTION_ID) {
    if (trainingRuns.length === 0 || preferZeroWeightSelection) {
      return ZERO_WEIGHT_OPTION_ID;
    }
  }
  if (trainingRuns.some((run) => run.id === selection)) {
    return selection;
  }
  const fallback = trainingRuns[0]?.id;
  return fallback ?? ZERO_WEIGHT_OPTION_ID;
}

function ensureSelectedTrainingRunId(): void {

  selectedTrainingRunId = resolveTrainingHistorySelection(selectedTrainingRunId);

}



function handleTrainingHistorySelectionChange(selection: string, shouldRunViewerPlayback = false): void {
  const isZeroSelection = selection === ZERO_WEIGHT_OPTION_ID;
  preferZeroWeightSelection = isZeroSelection;
  isZeroWeightsSelectionActive = isZeroSelection;
  const resolvedInput = isZeroSelection ? ZERO_WEIGHT_OPTION_ID : selection || null;
  selectedTrainingRunId = resolveTrainingHistorySelection(resolvedInput);
  syncTrainingHistorySelectValues();
  renderTrainingHistoryParams();
  applyStepLimitDefaultsForSelection(isZeroSelection, selectedTrainingRunId, true);
  if (selectedTrainingRunId === ZERO_WEIGHT_OPTION_ID) {
    clearHistoryView();
    renderCurrentEpisode();
  } else if (trainingParamsTab === 'history') {
    applySelectedHistoryRun();
  } else {
    clearHistoryView();
  }
  if (shouldRunViewerPlayback) {
    queueViewerPlayback(selectedTrainingRunId);
    attemptPendingViewerPlayback();
  }
}

function syncTrainingHistorySelectValues(): void {
  const viewerValue = selectedTrainingRunId ?? '';
  if (viewerHistorySelect && viewerHistorySelect.value !== viewerValue) {
    viewerHistorySelect.value = viewerValue;
  }
  if (!trainingHistorySelect) {
    return;
  }
  if (trainingHistorySelect.disabled) {
    trainingHistorySelect.value = '';
    return;
  }
  if (selectedTrainingRunId && selectedTrainingRunId !== ZERO_WEIGHT_OPTION_ID) {
    const hasOption = Array.from(trainingHistorySelect.options).some((option) => option.value === selectedTrainingRunId);
    if (hasOption) {
      if (trainingHistorySelect.value !== selectedTrainingRunId) {
        trainingHistorySelect.value = selectedTrainingRunId;
      }
      return;
    }
  }
  trainingHistorySelect.value = '';
  trainingHistorySelect.selectedIndex = -1;
}

function queueViewerPlayback(runId: string | null): void {
  if (!runId) {
    pendingViewerPlaybackRunId = null;
    return;
  }
  pendingViewerPlaybackRunId = runId;
}

function attemptPendingViewerPlayback(): void {
  if (!pendingViewerPlaybackRunId) {
    return;
  }
  if (!runner || !controllerClient) {
    return;
  }
  const runId = pendingViewerPlaybackRunId;
  pendingViewerPlaybackRunId = null;
  if (runId !== selectedTrainingRunId && runId) {
    handleTrainingHistorySelectionChange(runId, false);
  }
  handleViewerHistoryRun();
}

function handleViewerHistoryRun(): void {
  if (!selectedTrainingRunId) {
    return;
  }
  if (!runner || !controllerClient) {
    queueViewerPlayback(selectedTrainingRunId);
    setStatus('Playground is still initialising.');
    return;
  }
  if (selectedTrainingRunId === ZERO_WEIGHT_OPTION_ID) {
    const zeroWeights = buildZeroThetaWeights();
    if (!zeroWeights || zeroWeights.length === 0) {
      setStatus('Controller weights are not available yet.');
      return;
    }
    pendingViewerPlaybackRunId = null;
    trainingView?.setEpisodeSeries(buildZeroActionSeries());
    trainingView?.setRewardSeries(buildZeroRewardSeries());
    trainingView?.setRewardSeriesLength(null);
    renderTrainingWeights(zeroWeights);
    runRobotWithWeights(zeroWeights, ZERO_WEIGHT_OPTION_LABEL);
    return;
  }
  const run = getSelectedTrainingRun();
  if (!run) {
    setStatus('Select a training history run to replay its weights.');
    return;
  }
  if (!isViewingHistory || viewingHistoryRunId !== run.id) {
    const applied = applyHistoryRunSnapshot(run);
    if (!applied) {
      setStatus('Selected training run does not include recorded weights.');
      return;
    }
  }
  const episode = getFinalEpisode();
  if (!episode?.thetaWeights || episode.thetaWeights.length === 0) {
    setStatus('Selected training run does not include recorded weights.');
    trainingView?.setRewardSeries(buildZeroRewardSeries());
    trainingView?.setRewardSeriesLength(null);
    renderTrainingWeights(buildZeroThetaWeights());
    return;
  }
  pendingViewerPlaybackRunId = null;
  const baseLabel = run.label ? `History: ${run.label}` : 'History run';
  const label = Number.isFinite(episode.episode)
    ? `${baseLabel} (episode ${episode.episode})`
    : baseLabel;
  runEpisodeInPlayground(episode, label);
}

function getFinalHistoryEpisode(run: TrainingRunSnapshot | null): TrainingHistoryEpisode | null {
  if (!run?.history?.episodes?.length) {
    return null;
  }
  for (let index = run.history.episodes.length - 1; index >= 0; index -= 1) {
    const episode = run.history.episodes[index];
    if (Array.isArray(episode?.theta_weights) && episode.theta_weights.length) {
      return episode;
    }
  }
  return null;
}


function renderTrainingHistoryParams(): void {
  if (trainingHistoryParams) {
    trainingHistoryParams.innerHTML = '';
  }
  const run = getSelectedTrainingRun();
  const parameterSource = run?.history?.parameters ?? run?.parameters ?? null;
  const forceDefaultClamp = selectedTrainingRunId === ZERO_WEIGHT_OPTION_ID;
  syncViewerClampRangeFromParameters(parameterSource, forceDefaultClamp);
  if (!trainingHistoryParams || !trainingHistoryEmpty) {
    renderViewerParams(run, parameterSource);
    return;
  }
  if (!run) {
    trainingHistoryParams.hidden = true;
    trainingHistoryEmpty.hidden = false;
    trainingHistoryEmpty.textContent = selectedTrainingRunId === ZERO_WEIGHT_OPTION_ID
      ? "Zero weights are editable - tweak a joint row in the right-hand matrix to explore the robot's motion."
      : trainingHistoryEmptyDefaultText;
    renderViewerParams(null, null);
    return;
  }
  if (!parameterSource) {
    trainingHistoryParams.hidden = true;
    trainingHistoryEmpty.hidden = false;
    trainingHistoryEmpty.textContent = trainingHistoryEmptyDefaultText;
    renderViewerParams(run, null);
    return;
  }
  trainingHistoryEmpty.hidden = true;
  trainingHistoryEmpty.textContent = trainingHistoryEmptyDefaultText;
  trainingHistoryParams.hidden = false;
  TRAINING_PARAMETER_DEFS.forEach((definition) => {
    const term = document.createElement('dt');
    term.textContent = definition.label;
    const value = document.createElement('dd');
    const raw = parameterSource?.[definition.id];
    value.textContent = formatTrainingHistoryValue(raw);
    trainingHistoryParams.append(term, value);
  });
  renderViewerParams(run, parameterSource);
}


function renderViewerParams(
  runOverride?: TrainingRunSnapshot | null,
  parameterOverride?: TrainingParameterValues | null,
): void {
  if (!viewerParamsContainer || !viewerParamsList || !viewerParamsEmpty) {
    return;
  }
  const run = runOverride ?? getSelectedTrainingRun();
  const parameterSource =
    parameterOverride ?? (run ? run.history?.parameters ?? run.parameters ?? null : null);
  const isZeroSelection = selectedTrainingRunId === ZERO_WEIGHT_OPTION_ID;
  viewerParamsList.innerHTML = '';
  if (!run) {
    viewerParamsList.hidden = true;
    viewerParamsEmpty.hidden = false;
    viewerParamsEmpty.textContent = isZeroSelection
      ? "Zero weights are editable - tweak a joint row in the right-hand matrix to explore the robot's motion."
      : viewerParamsEmptyDefaultText;
    return;
  }
  if (!parameterSource) {
    viewerParamsList.hidden = true;
    viewerParamsEmpty.hidden = false;
    viewerParamsEmpty.textContent = 'Training parameters unavailable.';
    return;
  }
  viewerParamsEmpty.hidden = true;
  viewerParamsEmpty.textContent = viewerParamsEmptyDefaultText;
  viewerParamsList.hidden = false;
  TRAINING_PARAMETER_DEFS.forEach((definition) => {
    const row = document.createElement('div');
    row.className = 'viewer-params__row';
    const label = document.createElement('span');
    label.className = 'viewer-params__label';
    label.textContent = definition.label;
    const value = document.createElement('span');
    value.className = 'viewer-params__value';
    const raw = parameterSource?.[definition.id];
    value.textContent = formatTrainingHistoryValue(raw);
    row.append(label, value);
    viewerParamsList.append(row);
  });
}

function getSelectedTrainingRun(): TrainingRunSnapshot | null {
  if (!selectedTrainingRunId) {
    return null;
  }
  return trainingRuns.find((run) => run.id === selectedTrainingRunId) ?? null;
}

function formatTrainingHistoryValue(value: unknown): string {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toString() : '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Enabled' : 'Disabled';
  }
  if (typeof value === 'string') {
    return value.length ? value : '—';
  }
  return '—';
}

function recordCompletedTrainingRun(payload: TrainingHistoryExportPayload): void {
  const timestamp = Date.parse(payload.job.completed_at ?? payload.job.started_at ?? '');
  const snapshot: TrainingRunSnapshot = {
    id: payload.job.id,
    label: payload.job.label,
    timestamp: Number.isFinite(timestamp) ? timestamp : Date.now(),
    parameters: payload.parameters ? { ...payload.parameters } : null,
    history: payload,
  };
  trainingRuns = mergeTrainingRuns([snapshot], trainingRuns);
  selectedTrainingRunId = snapshot.id;
  preferZeroWeightSelection = false;
  lastCompletedHistoryPayload = payload;
  refreshTrainingHistoryUI();
  if (trainingParamsTab === 'history') {
    applySelectedHistoryRun();
  }
}

function mergeTrainingRuns(
  freshRuns: TrainingRunSnapshot[],
  existingRuns: TrainingRunSnapshot[],
): TrainingRunSnapshot[] {
  const merged = new Map<string, TrainingRunSnapshot>();
  existingRuns.forEach((run) => merged.set(run.id, run));
  freshRuns.forEach((run) => merged.set(run.id, run));
  return Array.from(merged.values()).sort(compareTrainingRuns);
}

function compareTrainingRuns(a: TrainingRunSnapshot, b: TrainingRunSnapshot): number {
  const aHasOrder = typeof a.orderIndex === 'number';
  const bHasOrder = typeof b.orderIndex === 'number';
  if (aHasOrder && bHasOrder) {
    return (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
  }
  if (aHasOrder && !bHasOrder) {
    return -1;
  }
  if (!aHasOrder && bHasOrder) {
    return 1;
  }
  return b.timestamp - a.timestamp;
}

function buildTrainingRunLabel(timestamp: number): string {
  return `Run - ${new Date(timestamp).toLocaleString()}`;
}

function buildTrainingRunLabelFromParameters(
  params?: TrainingParameterValues | null,
): string | null {
  if (!params) {
    return null;
  }
  const descriptors: string[] = [];
  let seedDescriptor: string | null = null;
  TRAINING_PARAMETER_DEFS.forEach((definition) => {
    const raw = params[definition.id];
    if (raw === undefined || raw === null || raw === '') {
      return;
    }
    if (isParameterValueAtDefault(raw, definition)) {
      return;
    }
    const label = definition.label ?? definition.id;
    if (definition.id === 'seed') {
      seedDescriptor = `${label} ${formatParameterValueForLabel(raw)}`;
      return;
    }
    descriptors.push(`${label} ${formatParameterValueForLabel(raw)}`);
  });
  if (!descriptors.length) {
    const seedLabel = formatSeedLabel(params.seed);
    return `Default parameters - seed ${seedLabel}`;
  }
  if (seedDescriptor) {
    descriptors.push(seedDescriptor);
  }
  return `Study on ${descriptors.join(', ')}`;
}

function isParameterValueAtDefault(
  value: unknown,
  definition: (typeof TRAINING_PARAMETER_DEFS)[number],
): boolean {
  if (!Object.prototype.hasOwnProperty.call(definition, 'defaultValue')) {
    return false;
  }
  return areParameterValuesEquivalent(value, definition.defaultValue);
}

function areParameterValuesEquivalent(value: unknown, baseline: unknown): boolean {
  if (typeof baseline === 'number') {
    const baselineNumber = baseline;
    const valueNumber = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(valueNumber)) {
      return false;
    }
    return Math.abs(valueNumber - baselineNumber) < 1e-9;
  }
  if (typeof baseline === 'boolean') {
    if (typeof value === 'boolean') {
      return value === baseline;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return (normalized === 'true') === baseline;
    }
    return false;
  }
  if (typeof baseline === 'string') {
    const normalizedBaseline = baseline.trim().toLowerCase();
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    return normalizedValue === normalizedBaseline;
  }
  return value === baseline;
}

function formatParameterValueForLabel(value: unknown): string {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return 'NaN';
    }
    const normalized = Number(value);
    if (Number.isInteger(normalized)) {
      return normalized.toString();
    }
    return normalized
      .toFixed(4)
      .replace(/0+$/, '')
      .replace(/\.$/, '');
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'string') {
    return value.replace(/\s+/g, '_').toLowerCase();
  }
  return String(value ?? '');
}

function formatSeedLabel(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toString();
  }
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue.toString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length) {
      return trimmed;
    }
  }
  return 'unknown';
}

function applySelectedHistoryRun(): void {
  const run = getSelectedTrainingRun();
  if (!run) {
    return;
  }
  if (!applyHistoryRunSnapshot(run)) {
    console.warn('Selected training run did not include history data.');
  }
}

function applyHistoryRunSnapshot(run: TrainingRunSnapshot): boolean {
  if (!run.history) {
    return false;
  }
  if (!isViewingHistory) {
    cacheLiveTrainingState();
  }
  isViewingHistory = true;
  viewingHistoryRunId = run.id;
  viewingHistoryParameters = run.history.parameters ? { ...run.history.parameters } : null;
  viewingHistoryClampRange = getHistoryClampRange(run.history);
  const weightShape = getHistoryWeightShape(run.history);
  const basisWindow = getHistoryBasisSnapshots(run.history, weightShape.cols);
  applyHistoryBasisSnapshots(basisWindow);
  const episodes = convertHistoryEpisodesToResults(run.history, basisWindow, weightShape);
  trainingEpisodes = episodes;
  currentEpisodeIndex = episodes.length ? episodes.length - 1 : -1;
  renderCurrentEpisode();
  updateTrainingActionToggle();
  syncActionPlotRange();
  return true;
}

function convertHistoryEpisodesToResults(
  payload: TrainingHistoryExportPayload,
  basisWindow: number[][] | null,
  weightShape: { rows: number; cols: number },
): EpisodeResult[] {
  const totalEpisodesFallback = payload.summary?.episodes_recorded ?? payload.episodes.length;
  const filteredEpisodes = filterEpisodeListByIndex(payload.episodes);
  return filteredEpisodes.map((episode) =>
    convertHistoryEpisodeToResult(episode, totalEpisodesFallback, basisWindow, weightShape),
  );
}

function convertHistoryEpisodeToResult(
  historyEpisode: TrainingHistoryEpisode,
  totalEpisodesFallback: number,
  basisWindow: number[][] | null,
  weightShape: { rows: number; cols: number },
): EpisodeResult {
  const thetaWeights = Array.isArray(historyEpisode.theta_weights) ? [...historyEpisode.theta_weights] : null;
  const thetaActions = basisWindow && thetaWeights
    ? computeHistoryActionMatrix(thetaWeights, basisWindow, weightShape)
    : null;
  const actions = thetaActions ?? createEmptyActionMatrix();
  return {
    episode: historyEpisode.episode,
    totalEpisodes: historyEpisode.total_episodes ?? totalEpisodesFallback,
    actions,
    thetaActions,
    thetaWeights,
    reward: historyEpisode.reward,
    totalReturn: historyEpisode.total_return,
  };
}

function getHistoryClampRange(payload: TrainingHistoryExportPayload): NumericRange | null {
  const metadataRangeRecord = payload.metadata as
    | { joint_range?: { min?: number; max?: number } }
    | undefined;
  const metadataRange = metadataRangeRecord?.joint_range ?? null;
  if (metadataRange) {
    const min = Number(metadataRange.min);
    const max = Number(metadataRange.max);
    if (Number.isFinite(min) && Number.isFinite(max) && max !== min) {
      return { min: Math.min(min, max), max: Math.max(min, max) };
    }
  }
  return parseJointClampRange(payload.parameters);
}

function getHistoryBasisSnapshots(
  payload: TrainingHistoryExportPayload,
  expectedColumns: number,
): number[][] | null {
  const metadataRecord = payload.metadata as { basis_snapshots?: number[][] } | undefined;
  const metadataWindow = metadataRecord?.basis_snapshots;
  if (Array.isArray(metadataWindow) && metadataWindow.length) {
    return metadataWindow.map((series) => (Array.isArray(series) ? [...series] : []));
  }
  const fallback = getDefaultBasisSnapshotWindow(expectedColumns);
  return fallback.length ? fallback : null;
}

function getDefaultBasisSnapshotWindow(expectedColumns: number): number[][] {
  if (!Number.isFinite(expectedColumns) || expectedColumns <= 0) {
    return [];
  }
  if (DEFAULT_BASIS_SNAPSHOT_WINDOW.length === expectedColumns) {
    return DEFAULT_BASIS_SNAPSHOT_WINDOW.map((series) => [...series]);
  }
  return [];
}

function getHistoryWeightShape(payload: TrainingHistoryExportPayload): { rows: number; cols: number } {
  const metadataRecord = payload.metadata as { weight_shape?: { rows?: number; cols?: number } } | undefined;
  const rowsMeta = Number(metadataRecord?.weight_shape?.rows);
  const colsMeta = Number(metadataRecord?.weight_shape?.cols);
  const rows = Number.isFinite(rowsMeta) && rowsMeta > 0 ? rowsMeta : ACTUATOR_NAMES.length;
  let cols = Number.isFinite(colsMeta) && colsMeta > 0 ? colsMeta : 0;
  if (!cols) {
    const firstWeightsLength = payload.episodes[0]?.theta_weights?.length ?? 0;
    cols = rows > 0 ? Math.max(1, Math.floor(firstWeightsLength / rows)) : 0;
  }
  return { rows, cols: Math.max(1, cols) };
}

function computeHistoryActionMatrix(
  thetaWeights: number[] | null,
  basisWindow: number[][],
  shape: { rows: number; cols: number },
): number[][] {
  const rows = shape.rows > 0 ? shape.rows : ACTUATOR_NAMES.length;
  const cols = shape.cols > 0 ? shape.cols : basisWindow.length;
  const steps = basisWindow[0]?.length ?? SNAPSHOT_WINDOW_LENGTH;
  const matrix: number[][] = Array.from({ length: rows }, () => Array(steps).fill(0));
  if (!thetaWeights || thetaWeights.length !== rows * cols || !basisWindow.length || !steps) {
    return matrix;
  }
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const basisSeries = basisWindow[col] ?? [];
      const weight = thetaWeights[row * cols + col] ?? 0;
      for (let step = 0; step < steps; step += 1) {
        matrix[row][step] += weight * (basisSeries[step] ?? 0);
      }
    }
  }
  return matrix;
}

function cacheLiveTrainingState(): void {
  cachedLiveTrainingEpisodes = trainingEpisodes.map(cloneEpisodeResult);
  cachedLiveEpisodeIndex = currentEpisodeIndex;
  cachedLiveBasisSnapshots = cloneBasisSnapshots();
}

function clearHistoryView(options?: { suppressRender?: boolean }): void {
  viewingHistoryRunId = null;
  viewingHistoryParameters = null;
  viewingHistoryClampRange = null;
  if (!isViewingHistory) {
    return;
  }
  trainingEpisodes = cachedLiveTrainingEpisodes.map(cloneEpisodeResult);
  currentEpisodeIndex = cachedLiveEpisodeIndex;
  cachedLiveTrainingEpisodes = [];
  cachedLiveEpisodeIndex = -1;
  isViewingHistory = false;
  if (!options?.suppressRender) {
    renderCurrentEpisode();
    syncActionPlotRange();
  }
  basisSnapshots.length = 0;
  cachedLiveBasisSnapshots.forEach((series, index) => {
    basisSnapshots[index] = [...series];
  });
  cachedLiveBasisSnapshots = [];
  updateTrainingActionToggle();
}

function cloneEpisodeResult(source: EpisodeResult): EpisodeResult {
  return {
    episode: source.episode,
    totalEpisodes: source.totalEpisodes,
    actions: source.actions.map((row) => [...row]),
    thetaActions: source.thetaActions ? source.thetaActions.map((row) => [...row]) : null,
    thetaWeights: source.thetaWeights ? [...source.thetaWeights] : null,
    reward: source.reward,
    totalReturn: source.totalReturn,
  };
}


async function loadTrainingHistoryFromFolder(): Promise<void> {
  try {
    const response = await fetch(TRAINING_HISTORY_MANIFEST_PATH, { cache: 'no-store' });
    if (!response.ok) {
      return;
    }
    const manifest = (await response.json()) as TrainingHistoryManifestPayload;
    const entries = Array.isArray(manifest?.runs) ? manifest.runs : [];
    if (!entries.length) {
      return;
    }
    const loaded: TrainingRunSnapshot[] = [];
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index];
      const snapshot = await fetchTrainingHistoryEntry(entry, index);
      if (snapshot) {
        loaded.push(snapshot);
      }
    }
    if (loaded.length) {
      trainingRuns = mergeTrainingRuns(loaded, trainingRuns);
      if (!selectedTrainingRunId) {
        const firstNonZeroRun = trainingRuns.find((run) => run.id !== ZERO_WEIGHT_OPTION_ID);
        selectedTrainingRunId = firstNonZeroRun?.id ?? null;
      }
      const shouldAutoRun = Boolean(selectedTrainingRunId && selectedTrainingRunId !== ZERO_WEIGHT_OPTION_ID);
      refreshTrainingHistoryUI();
      if (trainingParamsTab === 'history') {
        applySelectedHistoryRun();
      }
      if (shouldAutoRun && selectedTrainingRunId) {
        queueViewerPlayback(selectedTrainingRunId);
        attemptPendingViewerPlayback();
      }
    }
  } catch (error) {
    console.warn('Failed to load training history manifest', error);
  }
}

async function fetchTrainingHistoryEntry(
  entry: TrainingHistoryManifestEntry,
  orderIndex: number,
): Promise<TrainingRunSnapshot | null> {
  const path = resolveTrainingHistoryPath(entry.file);
  if (!path) {
    return null;
  }
  try {
    const response = await fetch(path, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as TrainingHistoryExportPayload;
    if (!payload?.job) {
      return null;
    }
    const timestamp = Date.parse(payload.job.completed_at ?? payload.job.started_at ?? '');
    const resolvedLabel = entry.label || payload.job.label || entry.file;
    return {
      id: payload.job.id || entry.id || path,
      label: resolvedLabel,
      timestamp: Number.isFinite(timestamp) ? timestamp : Date.now(),
      parameters: payload.parameters ? { ...payload.parameters } : null,
      history: payload,
      orderIndex,
    };
  } catch (error) {
    console.warn('Failed to load training history file', entry.file, error);
    return null;
  }
}

function resolveTrainingHistoryPath(pathSegment: string): string {
  if (!pathSegment) {
    return '';
  }
  if (/^https?:/i.test(pathSegment)) {
    return pathSegment;
  }
  if (pathSegment.startsWith('/')) {
    return pathSegment;
  }
  return `${TRAINING_HISTORY_DIR}/${pathSegment.replace(/^\/+/, '')}`;
}

async function resolveInitialWeightSet(): Promise<WeightSet> {
  if (trainingHistoryLoadPromise) {
    try {
      await trainingHistoryLoadPromise;
    } catch {
      // Manifest load failures are logged inside loadTrainingHistoryFromFolder
    }
  }
  ensureSelectedTrainingRunId();
  const zeroSelection = selectedTrainingRunId === ZERO_WEIGHT_OPTION_ID;
  let run = zeroSelection ? null : getSelectedTrainingRun();
  if (!run || !run.history) {
    run = findRunWithHistory();
  }
  if (!run || !run.history) {
    throw new Error('No training history weights are available. Add exports under training-history/.');
  }
  const shape = getHistoryWeightShape(run.history);
  const expectedLength = Math.max(1, shape.rows * shape.cols);
  if (zeroSelection) {
    return {
      values: new Float64Array(expectedLength),
      rows: shape.rows,
      cols: shape.cols,
    };
  }
  const finalEpisode = getFinalHistoryEpisode(run);
  const thetaWeights = normaliseThetaWeights(finalEpisode?.theta_weights);
  if (!thetaWeights || thetaWeights.length === 0) {
    throw new Error('Selected training run does not include recorded weights.');
  }
  return {
    values: coerceThetaWeightsLength(thetaWeights, expectedLength),
    rows: shape.rows,
    cols: shape.cols,
  };
}

function findRunWithHistory(): TrainingRunSnapshot | null {
  return trainingRuns.find((run) => Boolean(run.history)) ?? null;
}

function coerceThetaWeightsLength(weights: number[], expectedLength: number): Float64Array {
  const safeLength = Math.max(0, expectedLength);
  const values = new Float64Array(safeLength);
  for (let index = 0; index < safeLength; index += 1) {
    values[index] = weights[index] ?? 0;
  }
  if (weights.length !== safeLength) {
    console.warn('Training weights length (%d) does not match expected length (%d).', weights.length, safeLength);
  }
  return values;
}



function parseJointClampRange(source?: TrainingParameterValues | null): NumericRange | null {
  if (!source) {
    return null;
  }
  const rawMin = source.jointRangeMin;
  const rawMax = source.jointRangeMax;
  const min = typeof rawMin === 'number' ? rawMin : Number(rawMin);
  const max = typeof rawMax === 'number' ? rawMax : Number(rawMax);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return null;
  }
  return { min: Math.min(min, max), max: Math.max(min, max) };
}


function syncViewerClampRangeFromParameters(
  parameterSource: TrainingParameterValues | null,
  forceDefault = false,
): void {
  const resolved = parseJointClampRange(parameterSource);
  if (!resolved) {
    if (!forceDefault) {
      return;
    }
    applyClampRangeSelection({ ...DEFAULT_JOINT_CLAMP_RANGE });
    return;
  }
  applyClampRangeSelection(resolved);
}

function applyClampRangeSelection(range: NumericRange): void {
  if (jointClampRange.min === range.min && jointClampRange.max === range.max) {
    return;
  }
  jointClampRange = { ...range };
  setClampRangeInputValues(jointClampRange);
  applyJointClampRange();
}

function resolveActionPlotRange(): NumericRange {
  if (isViewingHistory) {
    if (viewingHistoryClampRange) {
      return viewingHistoryClampRange;
    }
    const historyRange = parseJointClampRange(viewingHistoryParameters);
    if (historyRange) {
      return historyRange;
    }
  }
  const jobRange = parseJointClampRange(lastRunTrainingParameters);
  if (jobRange && (Boolean(activeTrainingJobId) || trainingEpisodes.length > 0)) {
    return jobRange;
  }
  const editingRange = parseJointClampRange(trainingParameters);
  if (editingRange) {
    return editingRange;
  }
  if (jobRange) {
    return jobRange;
  }
  return { ...DEFAULT_ACTION_PLOT_RANGE };
}

function syncActionPlotRange(): void {
  actionPlotRange = resolveActionPlotRange();
  if (trainingView) {
    trainingView.setActionRange(actionPlotRange);
  }
  renderSelectedWeightPlots();
}

function getActionPlotRange(): NumericRange {
  return actionPlotRange;
}


function showView(view: ViewName): void {
  currentView = view;
  viewSections.forEach((section) => {
    section.classList.toggle('view--active', section.dataset.view === view);
  });
  tabButtons.forEach((button) => {
    const isActive = button.dataset.tab === view;
    button.classList.toggle('app-tab--active', isActive);
    if (isActive) {
      button.setAttribute('aria-current', 'page');
    } else {
      button.removeAttribute('aria-current');
    }
  });
  if (view === 'training') {
    trainingView?.render();
  }
}

function startSimulation(statusMessage = 'Simulation running'): void {
  if (!runner || isRunning) {
    return;
  }
  scheduleStepLimitPause();
  runner.start();
  isRunning = true;
  if (startButton) {
    startButton.textContent = 'Pause';
    startButton.dataset.running = 'true';
  }
  if (statusMessage) {
    setStatus(statusMessage);
  }
}

function pauseSimulation(statusMessage = 'Simulation paused'): void {
  if (!runner) {
    return;
  }
  runner.pause();
  clearStepLimitPause();
  isRunning = false;
  if (startButton) {
    startButton.textContent = 'Start';
    startButton.dataset.running = 'false';
  }
  if (statusMessage) {
    setStatus(statusMessage);
  }
}


function attachInspectorToRow(rowElement: HTMLElement): void {
  if (!weightInspector) {
    return;
  }
  weightInspector.hidden = false;
  weightInspector.classList.add('weight-inspector--inline');
  rowElement.insertAdjacentElement('beforebegin', weightInspector);
}

function resetInspectorPosition(): void {
  if (!weightInspector) {
    return;
  }
  weightInspector.classList.remove('weight-inspector--inline');
  if (weightInspectorHost instanceof HTMLElement) {
    weightInspectorHost.appendChild(weightInspector);
  }
  weightInspector.hidden = true;
}

async function bootstrap(): Promise<void> {
  try {
    setStatus('Loading training history weights...');
    weightSet = await resolveInitialWeightSet();

    setStatus('Initialising controller...');
    controllerClient = await SMEControllerClient.create(weightSet.values);
    renderWeightMatrix();

    setStatus('Booting MuJoCo...');
    runner = new MuJoCoRunner({
      xmlAsset: 'phantomx.xml',
      assetFilenames: PHANTOMX_ASSETS.slice(),
      controlDt: CONTROL_DT,
    });
    await runner.initialize(controllerClient);

    const canvas = document.getElementById('viewer-canvas');
    if (!(canvas instanceof HTMLCanvasElement)) {
      throw new Error('viewer-canvas not found');
    }
    viewer = new PhantomXViewer(canvas, { runner, theme: DEFAULT_THEME });
    await viewer.whenReady();

    unsubscribeRunner = runner.subscribe(handleFrame);
    setupControls();
    runner.setClampOutputs(Boolean(clampToggle?.checked));
    applyJointClampRange();
    resetSignalBuffers();
    runner.reset();
    runner.dropBase();
    isBaseLifted = false;
    updateBaseToggleLabel();
    startSimulation();
    attemptPendingViewerPlayback();
  } catch (error) {
    console.error(error);
    setStatus(error instanceof Error ? error.message : 'Failed to initialise playground');
  }
}


function setupControls(): void {

  startButton?.addEventListener('click', () => {

    if (!runner) {

      return;

    }

    if (isRunning) {

      pauseSimulation();

    } else {

      startSimulation();

    }

  });



  stepLimitInput?.addEventListener('change', () => {

    handleStepLimitInput();

  });



  clampRangeMinInput?.addEventListener('change', () => {

    handleClampRangeInput();

  });



  clampRangeMaxInput?.addEventListener('change', () => {

    handleClampRangeInput();

  });



  resetButton?.addEventListener('click', () => {

    pauseSimulation('');

    runner?.reset();

    runner?.dropBase();

    isBaseLifted = false;

    updateBaseToggleLabel();

    resetSignalBuffers();

    setStatus('Simulation reset');

  });



  baseToggleButton?.addEventListener('click', () => {

    if (!runner) {

      return;

    }

    if (isBaseLifted) {

      runner.dropBase();

      isBaseLifted = false;

      setStatus('Base dropped');

    } else {

      runner.liftBase();

      isBaseLifted = true;

      setStatus('Base lifted');

    }

    updateBaseToggleLabel();

  });



  clampToggle?.addEventListener('change', () => {

    const isEnabled = Boolean(clampToggle.checked);

    runner?.setClampOutputs(isEnabled);

    applyJointClampRange();

    if (clampNode) {

      clampNode.textContent = isEnabled ? 'enabled' : 'disabled';

    }

  });



  speedSlider?.addEventListener('input', () => {

    const value = Number(speedSlider.value);

    runner?.setPlaybackMultiplier(value);

    if (speedLabel) {

      speedLabel.textContent = `${value.toFixed(2)}x`;

    }

  });

}



function resetSignalBuffers(): void {
  cpgHistory.length = 0;
  basisHistory.length = 0;
  basisSnapshots.length = 0;
}

function handleFrame(snapshot: FrameSnapshot): void {
  latestSnapshotStep = snapshot.step;
  enforceStepLimit(snapshot.step);
  viewer?.update(snapshot);
  updateTelemetry(snapshot);
  appendSignalSample(basisHistory, snapshot.basisState);
  appendSignalSample(cpgHistory, snapshot.cpgState);
  captureSnapshotSample(basisSnapshots, snapshot.basisState, weightSet?.cols ?? snapshot.basisState.length);
  renderSignalChart(basisCanvas, basisHistory);
  renderSignalLegend(basisLegend, basisHistory, 'B');
  renderSignalChart(cpgCanvas, cpgHistory);
  renderSignalLegend(cpgLegend, cpgHistory, 'C');
  renderSelectedWeightPlots();
  trainingView?.render();
}

function updateTelemetry(snapshot: FrameSnapshot): void {
  simTimeNode && (simTimeNode.textContent = `${snapshot.simTime.toFixed(2)} s`);
  stepNode && (stepNode.textContent = `${snapshot.step}`);
  const basePos = snapshot.basePosition;
  const baseHeight = basePos && basePos.length > 2 ? basePos[2] : 0;
  const baseX = basePos && basePos.length > 0 ? basePos[0] : 0;
  const baseY = basePos && basePos.length > 1 ? basePos[1] : 0;
  baseXNode && (baseXNode.textContent = `${baseX.toFixed(3)} m`);
  baseYNode && (baseYNode.textContent = `${baseY.toFixed(3)} m`);
  baseNode && (baseNode.textContent = `${baseHeight.toFixed(3)} m`);
}

function appendSignalSample(
  history: number[][],
  sample: Float64Array,
  channelLimit = MAX_SIGNAL_CHANNELS,
  maxSamples = SIGNAL_HISTORY_LENGTH,
): void {
  const channels = Math.min(sample.length, channelLimit);
  for (let channel = 0; channel < channels; channel += 1) {
    if (!history[channel]) {
      history[channel] = [];
    }
    history[channel].push(sample[channel]);
    if (history[channel].length > maxSamples) {
      history[channel].shift();
    }
  }
}

function captureSnapshotSample(
  snapshots: number[][],
  sample: Float64Array,
  channelLimit: number,
  maxSamples = SNAPSHOT_CAPTURE_LENGTH,
): void {
  const channels = Math.min(sample.length, channelLimit);
  for (let channel = 0; channel < channels; channel += 1) {
    if (!snapshots[channel]) {
      snapshots[channel] = [];
    }
    const series = snapshots[channel];
    if (series.length >= maxSamples) {
      continue;
    }
    series.push(sample[channel]);
  }
}

function clampValueToRange(value: number, min: number, max: number): number {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

function captureBasisSnapshotWindow(): number[][] {
  const windowStart = BASIS_SNAPSHOT_OFFSET;
  const windowEnd = windowStart + SNAPSHOT_WINDOW_LENGTH;
  if (!basisSnapshots.length) {
    return [];
  }
  return basisSnapshots.map((series) => {
    if (!Array.isArray(series) || series.length < windowEnd) {
      return Array.from({ length: SNAPSHOT_WINDOW_LENGTH }, () => 0);
    }
    return series.slice(windowStart, windowEnd);
  });
}

function applyHistoryBasisSnapshots(snapshotWindow: number[][] | null | undefined): void {
  basisSnapshots.length = 0;
  if (!snapshotWindow || !snapshotWindow.length) {
    return;
  }
  snapshotWindow.forEach((series, index) => {
    const buffer = Array.from({ length: SNAPSHOT_CAPTURE_LENGTH }, () => 0);
    const windowLength = Math.min(series?.length ?? 0, SNAPSHOT_WINDOW_LENGTH);
    for (let i = 0; i < windowLength; i += 1) {
      buffer[BASIS_SNAPSHOT_OFFSET + i] = series?.[i] ?? 0;
    }
    basisSnapshots[index] = buffer;
  });
}

function cloneBasisSnapshots(): number[][] {
  return basisSnapshots.map((series) => (Array.isArray(series) ? [...series] : []));
}

function renderSignalChart(
  canvas: HTMLCanvasElement | null,
  history: number[][],
  options?: SignalChartOptions,
): void {
  if (!canvas) {
    return;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  const width = canvas.width;
  const height = canvas.height;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, width, height);
  const channels = history.filter((series) => series && series.length);
  if (!channels.length) {
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 12px Inter, sans-serif';
    ctx.fillText('AWAITING DATA', 10, height / 2);
    return;
  }
  let min: number;
  let max: number;
  if (options?.range && Number.isFinite(options.range.min) && Number.isFinite(options.range.max) && options.range.max > options.range.min) {
    min = options.range.min;
    max = options.range.max;
  } else {
    let computedMin = Infinity;
    let computedMax = -Infinity;
    channels.forEach((series) => {
      series.forEach((value) => {
        computedMin = Math.min(computedMin, value);
        computedMax = Math.max(computedMax, value);
      });
    });
    if (!Number.isFinite(computedMin) || !Number.isFinite(computedMax) || computedMin === computedMax) {
      computedMin -= 1;
      computedMax += 1;
    }
    min = computedMin;
    max = computedMax;
  }
  const shouldClamp = Boolean(options?.clampToRange);
  const pad = 8;
  const range = max - min;
  const maxLength = Math.max(...channels.map((series) => series.length));
  const stepX = (width - pad * 2) / Math.max(maxLength - 1, 1);
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = 1;
  if (min <= 0 && max >= 0) {
    ctx.beginPath();
    const zeroY = height - pad - ((0 - min) / range) * (height - pad * 2);
    ctx.moveTo(pad, zeroY);
    ctx.lineTo(width - pad, zeroY);
    ctx.stroke();
  }
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 10px Inter, sans-serif';
  ctx.fillText(max.toFixed(2), pad, pad + 10);
  ctx.fillText(min.toFixed(2), pad, height - pad);
  channels.forEach((series, idx) => {
    ctx.strokeStyle = SIGNAL_COLORS[idx % SIGNAL_COLORS.length];
    ctx.lineWidth = 2;
    ctx.beginPath();
    series.forEach((value, sampleIdx) => {
      const x = pad + sampleIdx * stepX;
      const sampleValue = shouldClamp ? clampValueToRange(value, min, max) : value;
      const normalized = (sampleValue - min) / range;
      const y = height - pad - normalized * (height - pad * 2);
      if (sampleIdx === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
  });
}

function renderSignalLegend(container: Element | null, history: number[][], prefix: string): void {
  if (!(container instanceof HTMLElement)) {
    return;
  }
  container.innerHTML = '';
  const channels = history.filter((series) => series && series.length);
  if (!channels.length) {
    const span = document.createElement('span');
    span.textContent = 'AWAITING DATA';
    container.appendChild(span);
    return;
  }
  channels.forEach((series, idx) => {
    const item = document.createElement('div');
    item.className = 'legend-item';
    const swatch = document.createElement('span');
    swatch.className = 'legend-swatch';
    swatch.style.backgroundColor = SIGNAL_COLORS[idx % SIGNAL_COLORS.length];
    const label = document.createElement('span');
    label.textContent = `${prefix}${idx + 1}`;
    item.append(swatch, label);
    container.appendChild(item);
  });
}

function renderWeightMatrix(): void {

  if (!(weightMatrix instanceof HTMLElement) || !weightSet) {

    return;

  }

  weightMatrix.innerHTML = '';

  weightRowElements.length = 0;

  weightCellButtons.length = 0;

  const header = document.createElement('div');

  header.className = 'weight-matrix__header';

  const headerLabel = document.createElement('span');

  headerLabel.className = 'weight-matrix__row-label';

  headerLabel.textContent = 'Joint';

  const headerCells = document.createElement('div');

  headerCells.className = 'weight-matrix__cell-labels';

  ['Pose 1', 'Pose 2', 'Pose 3', 'Pose 4'].forEach((poseLabel) => {

    const span = document.createElement('span');

    span.textContent = poseLabel;

    headerCells.appendChild(span);

  });

  header.append(headerLabel, headerCells);

  weightMatrix.appendChild(header);

  for (let row = 0; row < weightSet.rows; row += 1) {

    const rowElement = document.createElement('div');

    rowElement.className = 'weight-matrix__row';

    const label = document.createElement('span');

    label.className = 'weight-matrix__row-label';

    label.textContent = formatJointLabel(row);

    const cellContainer = document.createElement('div');

    cellContainer.className = 'weight-matrix__cells';

    weightCellButtons[row] = [];

    for (let col = 0; col < weightSet.cols; col += 1) {

      const cellButton = document.createElement('button');

      cellButton.type = 'button';

      cellButton.className = 'weight-matrix__cell';

      cellButton.textContent = getWeightValue(row, col).toFixed(3);

      cellButton.addEventListener('click', () => {

        selectWeightRow(row);

      });

      cellContainer.appendChild(cellButton);

      weightCellButtons[row][col] = cellButton;

    }

    rowElement.append(label, cellContainer);

    weightMatrix.appendChild(rowElement);

    weightRowElements[row] = rowElement;

  }

  if (selectedWeightRow >= 0 && selectedWeightRow < weightSet.rows && weightInspector) {

    const activeRow = weightRowElements[selectedWeightRow];

    if (activeRow) {

      attachInspectorToRow(activeRow);

    }

  } else {

    resetInspectorPosition();

  }

}

function selectWeightRow(row: number): void {

  if (!weightSet) {

    return;

  }

  selectedWeightRow = row;

  weightRowElements.forEach((element, idx) => {

    element?.classList.toggle('weight-matrix__row--active', idx === row);

  });

  const targetRow = weightRowElements[row];

  if (targetRow) {

    attachInspectorToRow(targetRow);

  } else {

    resetInspectorPosition();

  }

  if (weightTitle) {

    weightTitle.textContent = formatJointLabel(row);

  }

  renderWeightSliders(row);

  renderSelectedWeightPlots();

  trainingView?.render();

}



function renderWeightSliders(row: number): void {
  if (!(weightSliders instanceof HTMLElement) || !weightSet) {
    return;
  }
  weightSliders.innerHTML = '';
  for (let col = 0; col < weightSet.cols; col += 1) {
    const sliderWrapper = document.createElement('label');
    sliderWrapper.className = 'weight-slider';
    const labelLine = document.createElement('div');
    labelLine.className = 'weight-slider__label';
    const name = document.createElement('span');
    name.textContent = `Pose ${col + 1}`;
    const valueDisplay = document.createElement('span');
    valueDisplay.className = 'weight-slider__value';
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = `${WEIGHT_SLIDER_MIN}`;
    slider.max = `${WEIGHT_SLIDER_MAX}`;
    slider.step = `${WEIGHT_SLIDER_STEP}`;
    const weightValue = getWeightValue(row, col);
    slider.value = `${weightValue}`;
    valueDisplay.textContent = weightValue.toFixed(3);
    slider.addEventListener('input', () => {
      const nextValue = Number(slider.value);
      valueDisplay.textContent = nextValue.toFixed(3);
      updateWeightValue(row, col, nextValue);
    });
    labelLine.append(name, valueDisplay);
    sliderWrapper.append(labelLine, slider);
    weightSliders.appendChild(sliderWrapper);
  }
}

function getWeightValue(row: number, col: number): number {
  if (!weightSet) {
    return 0;
  }
  return weightSet.values[row * weightSet.cols + col] ?? 0;
}

function updateWeightValue(row: number, col: number, value: number): void {

  if (!weightSet) {

    return;

  }

  const index = row * weightSet.cols + col;

  weightSet.values[index] = value;

  controllerClient?.loadWeights(weightSet.values);

  updateWeightRowCells(row);

  renderSelectedWeightPlots();

  trainingView?.render();

}



function updateWeightRowCells(row: number): void {

  const rowCells = weightCellButtons[row];

  if (!rowCells) {

    return;

  }

  for (let col = 0; col < rowCells.length; col += 1) {

    const button = rowCells[col];

    if (button) {

      button.textContent = getWeightValue(row, col).toFixed(3);

    }

  }

}

function clearCanvas(canvas: HTMLCanvasElement | null): void {
  if (!canvas) {
    return;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

function closeWeightInspector(): void {
  selectedWeightRow = -1;
  weightRowElements.forEach((element) => {
    element?.classList.remove('weight-matrix__row--active');
  });
  resetInspectorPosition();
  if (weightTitle) {
    weightTitle.textContent = DEFAULT_WEIGHT_TITLE;
  }
  if (weightSliders) {
    weightSliders.innerHTML = '';
  }
  if (weightBasisLegend instanceof HTMLElement) {
    weightBasisLegend.innerHTML = '';
  }
  clearCanvas(weightBasisCanvas);
  clearCanvas(weightActionCanvas);
}

function computeActionSeries(row: number): number[] {
  if (!weightSet) {
    return [];
  }
  const lengths = basisSnapshots.map((series) => (Array.isArray(series) ? series.length : 0));
  const maxLength = lengths.length ? Math.max(...lengths) : 0;
  const available = maxLength - BASIS_SNAPSHOT_OFFSET;
  const steps = Math.min(SNAPSHOT_WINDOW_LENGTH, Math.max(available, 0));
  if (!steps) {
    return [];
  }
  const snapshot: number[] = [];
  for (let step = 0; step < steps; step += 1) {
    let value = 0;
    for (let col = 0; col < weightSet.cols; col += 1) {
      const series = basisSnapshots[col];
      const sampleIndex = BASIS_SNAPSHOT_OFFSET + step;
      const basisValue = series?.[sampleIndex] ?? 0;
      value += getWeightValue(row, col) * basisValue;
    }
    snapshot.push(value);
  }
  return snapshot;
}

function renderSelectedWeightPlots(): void {
  if (!weightSet || selectedWeightRow < 0) {
    return;
  }
  const basisSeries: number[][] = [];
  const windowStart = BASIS_SNAPSHOT_OFFSET;
  const windowEnd = windowStart + SNAPSHOT_WINDOW_LENGTH;
  for (let i = 0; i < weightSet.cols; i += 1) {
    const series = basisSnapshots[i];
    basisSeries.push(series ? series.slice(windowStart, windowEnd) : []);
  }
  renderSignalChart(weightBasisCanvas, basisSeries);
  renderSignalLegend(weightBasisLegend, basisSeries, 'B');
  const actionSeries = computeActionSeries(selectedWeightRow);
  const snapshot = actionSeries.length ? [actionSeries] : [];
  renderSignalChart(weightActionCanvas, snapshot, { range: getActionPlotRange(), clampToRange: true });
}


function buildTrainingPayload(values: TrainingParameterValues): Record<string, unknown> {
  const episodes = Number(values?.episodes) || 1;
  return {
    episodes,
    horizon: Number(values?.horizon) || SNAPSHOT_WINDOW_LENGTH,
    reward: String(values?.reward ?? 'forward'),
    lr_theta: Number(values?.lrTheta) || 1e-2,
    lr_sigma: Number(values?.lrSigma) || 1e-2,
    init_sigma: Number(values?.initSigma) || 0.01,
    advantage_window: Number(values?.advantageWindow) || 4,
    update_episodes: Number(values?.updateEpisodes) || 1,
    seed: Number(values?.seed) || 0,
    joint_range_min: Number(values?.jointRangeMin) || -1,
    joint_range_max: Number(values?.jointRangeMax) || 1,
    algo: 'agol',
    metadata: {},
  };
}

function getSpeedrunSpeed(): number {
  const rawValue = speedrunSlider ? Number(speedrunSlider.value) : Number.NaN;
  if (!Number.isFinite(rawValue) || rawValue <= 0) {
    return SPEEDRUN_DEFAULT_SPEED;
  }
  return rawValue;
}

function updateSpeedrunSpeedLabel(): void {
  if (speedrunSpeedLabel) {
    speedrunSpeedLabel.textContent = `${getSpeedrunSpeed().toFixed(1)} eps/s`;
  }
}

function updateSpeedrunButtonState(): void {
  if (!speedrunButton) {
    return;
  }
  speedrunButton.textContent = isSpeedrunning ? 'Stop speedrun' : 'Speedrun';
  speedrunButton.classList.toggle('is-active', isSpeedrunning);
  speedrunButton.disabled = trainingEpisodes.length === 0;
}

function clearSpeedrunTimer(): void {
  if (speedrunHandle) {
    window.clearTimeout(speedrunHandle);
    speedrunHandle = null;
  }
}

function scheduleSpeedrunTick(): void {
  if (!isSpeedrunning) {
    return;
  }
  if (!trainingEpisodes.length) {
    stopSpeedrun();
    return;
  }
  clearSpeedrunTimer();
  const speed = Math.max(getSpeedrunSpeed(), 0.1);
  const interval = Math.max(100, Math.floor(1000 / speed));
  speedrunHandle = window.setTimeout(() => {
    advanceSpeedrunEpisode();
    scheduleSpeedrunTick();
  }, interval);
}

function advanceSpeedrunEpisode(): void {
  if (!trainingEpisodes.length) {
    stopSpeedrun();
    return;
  }
  const lastIndex = trainingEpisodes.length - 1;
  if (currentEpisodeIndex < 0) {
    currentEpisodeIndex = 0;
  } else if (currentEpisodeIndex >= lastIndex) {
    stopSpeedrun();
    return;
  } else {
    currentEpisodeIndex += 1;
  }
  renderCurrentEpisode();
  if (currentEpisodeIndex >= lastIndex) {
    stopSpeedrun();
  }
}

function startSpeedrun(): void {
  if (!trainingEpisodes.length) {
    return;
  }
  const lastIndex = trainingEpisodes.length - 1;
  if (currentEpisodeIndex < 0 || currentEpisodeIndex >= lastIndex) {
    currentEpisodeIndex = 0;
    renderCurrentEpisode();
  }
  if (!isSpeedrunning) {
    isSpeedrunning = true;
    updateSpeedrunButtonState();
  }
  scheduleSpeedrunTick();
}

function stopSpeedrun(): void {
  if (!isSpeedrunning && !speedrunHandle) {
    updateSpeedrunButtonState();
    return;
  }
  isSpeedrunning = false;
  clearSpeedrunTimer();
  updateSpeedrunButtonState();
}

function restartSpeedrunTimer(): void {
  if (!isSpeedrunning) {
    return;
  }
  scheduleSpeedrunTick();
}

function hasThetaActionData(): boolean {
  return trainingEpisodes.some((episode) => {
    if (!episode.thetaActions) {
      return false;
    }
    return episode.thetaActions.some((row) => row.length);
  });
}

function updateTrainingActionToggle(): void {
  const hasEpisodes = trainingEpisodes.length > 0;
  const thetaAvailable = hasThetaActionData();
  if (isViewingHistory) {
    trainingActionMode = 'theta';
  } else if (trainingActionMode === 'theta' && !thetaAvailable) {
    trainingActionMode = 'perturbed';
  }
  if (actionToggle) {
    actionToggle.hidden = !hasEpisodes;
  }
  actionModeButtons.forEach((button) => {
    const target = (button.dataset.actionMode as TrainingActionMode | undefined) ?? 'perturbed';
    const historyLocked = isViewingHistory && target === 'perturbed';
    const isActive = trainingActionMode === target;
    button.classList.toggle('is-active', isActive);
    button.disabled = !hasEpisodes
      || (target === 'theta' && !thetaAvailable)
      || historyLocked;
    if (historyLocked) {
      button.title = 'Perturbed actions are unavailable when viewing training history.';
    } else {
      button.removeAttribute('title');
    }
  });
}

function setTrainingActionMode(mode: TrainingActionMode): void {
  if (isViewingHistory) {
    mode = 'theta';
  }
  const thetaAvailable = hasThetaActionData();
  const nextMode: TrainingActionMode = mode === 'theta' && !thetaAvailable ? 'perturbed' : mode;
  if (trainingActionMode === nextMode) {
    updateTrainingActionToggle();
    renderCurrentEpisode();
    return;
  }
  trainingActionMode = nextMode;
  updateTrainingActionToggle();
  renderCurrentEpisode();
}

function getEpisodeSeriesForMode(result: EpisodeResult): number[][] {
  if (trainingActionMode === 'theta') {
    return result.thetaActions ?? result.actions;
  }
  return result.actions;
}

function determineThetaColumns(length: number, rowCount: number): number {
  if (weightSet && weightSet.cols > 0) {
    return weightSet.cols;
  }
  if (rowCount <= 0) {
    return length || 1;
  }
  return Math.max(1, Math.ceil(length / rowCount));
}

function buildThetaWeightMatrix(weights: number[]): number[][] {
  const rows = ACTUATOR_NAMES.length;
  if (!weights.length) {
    return [];
  }
  if (rows <= 0) {
    return [weights];
  }
  const cols = determineThetaColumns(weights.length, rows);
  const matrix: number[][] = [];
  for (let row = 0; row < rows; row += 1) {
    const start = row * cols;
    const slice = weights.slice(start, start + cols);
    matrix.push(slice);
  }
  return matrix;
}

function renderTrainingWeights(weights: number[] | null): void {
  if (!trainingWeightsContainer) {
    return;
  }
  const hasWeights = Array.isArray(weights) && weights.length > 0;
  trainingWeightsContainer.innerHTML = '';
  if (trainingWeightsEmpty) {
    trainingWeightsEmpty.hidden = hasWeights || shouldShowZeroWeightsPlaceholder();
  }
  trainingWeightsPanel?.classList.toggle('panel--empty', !hasWeights && !shouldShowZeroWeightsPlaceholder());
  if (!hasWeights || !weights?.length) {
    if (shouldShowZeroWeightsPlaceholder()) {
      weights = buildZeroThetaWeights();
      if (!weights) {
        return;
      }
    } else {
      return;
    }
  }
  const matrix = buildThetaWeightMatrix(weights);
  matrix.forEach((rowValues, rowIndex) => {
    const rowElement = document.createElement('div');
    rowElement.className = 'training-weights__row';
    const label = document.createElement('div');
    label.className = 'training-weights__label';
    label.textContent = formatJointLabel(rowIndex);
    const cellsElement = document.createElement('div');
    cellsElement.className = 'training-weights__cells';
    if (rowValues.length) {
      rowValues.forEach((value) => {
        const cell = document.createElement('div');
        cell.className = 'training-weights__cell';
        cell.textContent = value.toFixed(3);
        cellsElement.appendChild(cell);
      });
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'training-weights__cell training-weights__cell--empty';
      placeholder.textContent = 'N/A';
      cellsElement.appendChild(placeholder);
    }
    rowElement.append(label, cellsElement);
    trainingWeightsContainer.appendChild(rowElement);
  });
}


function updateTrainingWeightButtons(): void {
  const episode = currentEpisodeIndex >= 0 ? trainingEpisodes[currentEpisodeIndex] : null;
  const weights = episode?.thetaWeights;
  const hasSelectedWeights = Array.isArray(weights) && weights.length > 0;
  if (trainingRunCurrentButton) {
    trainingRunCurrentButton.disabled = !hasSelectedWeights;
    trainingRunCurrentButton.title = hasSelectedWeights
      ? 'Switch to the playground and run the robot with these weights.'
      : 'Select an episode with weights to run it in the playground.';
  }
  const finalEpisode = getFinalEpisode();
  const hasFinalWeights = Array.isArray(finalEpisode?.thetaWeights) && finalEpisode.thetaWeights.length > 0;
  if (trainingRunFinalButton) {
    trainingRunFinalButton.disabled = !hasFinalWeights;
    trainingRunFinalButton.title = hasFinalWeights
      ? 'Run the playground robot using the final weights from this training job.'
      : 'Final weights become available once training completes.';
  }
  if (trainingSaveButton) {
    trainingSaveButton.disabled = !hasSelectedWeights;
    trainingSaveButton.title = hasSelectedWeights
      ? 'Download weights and parameters for the selected episode.'
      : 'Weights become available once training completes.';
  }
  const hasAnyWeights = hasEpisodeWeightData();
  if (trainingSaveAllButton) {
    trainingSaveAllButton.disabled = !hasAnyWeights;
    trainingSaveAllButton.title = hasAnyWeights
      ? 'Download weights for every recorded episode.'
      : 'Weights become available once training completes.';
  }
}

function handleSaveWeights(): void {
  if (!trainingSaveButton) {
    return;
  }
  const episode = currentEpisodeIndex >= 0 ? trainingEpisodes[currentEpisodeIndex] : null;
  if (!episode?.thetaWeights || episode.thetaWeights.length === 0) {
    return;
  }
  const payload = buildTrainingWeightExportPayload(episode);
  const filename = buildWeightExportFilename(episode);
  downloadJsonFile(payload, filename);
}


function handleSaveAllWeights(): void {
  if (!trainingSaveAllButton || !hasEpisodeWeightData()) {
    return;
  }
  const payload = getLatestTrainingHistoryExportPayload();
  if (!payload) {
    return;
  }
  const filename = buildTrainingHistoryFilename(payload);
  downloadJsonFile(payload, filename);
}

function handleRunCurrentEpisodeWeights(): void {
  const episode = getEpisodeAt(currentEpisodeIndex);
  runEpisodeInPlayground(episode, 'Running current episode weights');
}

function handleRunFinalEpisodeWeights(): void {
  const episode = getFinalEpisode();
  runEpisodeInPlayground(episode, 'Running final learned weights');
}

function runEpisodeInPlayground(episode: EpisodeResult | null, baseLabel: string): void {
  if (!episode?.thetaWeights || episode.thetaWeights.length === 0) {
    return;
  }
  const label = Number.isFinite(episode.episode)
    ? `${baseLabel} (episode ${episode.episode})`
    : baseLabel;
  runRobotWithWeights(episode.thetaWeights, label);
}

function runRobotWithWeights(weights: number[], label: string): void {
  if (!runner || !controllerClient) {
    console.warn('Playground is not ready for weight playback.');
    setStatus('Playground is still initialising.');
    return;
  }
  if (!applyWeightsToController(weights)) {
    return;
  }
  stopSpeedrun();
  showView('playground');
  pauseSimulation('');
  controllerClient.reset();
  runner.reset();
  runner.dropBase();
  isBaseLifted = false;
  updateBaseToggleLabel();
  resetSignalBuffers();
  startSimulation(label);
}

function applyWeightsToController(weights: number[]): boolean {
  if (!weightSet || !controllerClient) {
    console.warn('Controller weights are not available yet.');
    setStatus('Controller weights are not available yet.');
    return false;
  }
  const expectedLength = weightSet.rows * weightSet.cols;
  if (weights.length !== expectedLength) {
    console.warn('Training weights have length %d but controller expects %d.', weights.length, expectedLength);
    setStatus('Training weights do not match the controller shape.');
    return false;
  }
  weightSet.values.set(weights);
  controllerClient.loadWeights(weightSet.values);
  renderWeightMatrix();
  if (selectedWeightRow >= 0 && selectedWeightRow < weightSet.rows) {
    selectWeightRow(selectedWeightRow);
  } else {
    renderSelectedWeightPlots();
  }
  return true;
}

function hasEpisodeWeightData(): boolean {
  return trainingEpisodes.some(
    (episode) => Array.isArray(episode.thetaWeights) && episode.thetaWeights.length > 0,
  );
}

function getEpisodeAt(index: number): EpisodeResult | null {
  if (index < 0 || index >= trainingEpisodes.length) {
    return null;
  }
  return trainingEpisodes[index];
}

function getFinalEpisode(): EpisodeResult | null {
  if (!trainingEpisodes.length) {
    return null;
  }
  return trainingEpisodes[trainingEpisodes.length - 1];
}

function shouldShowZeroWeightsPlaceholder(): boolean {
  if (isZeroWeightsSelectionActive) {
    return true;
  }
  if (activeTrainingJobId) {
    return true;
  }
  return trainingParamsTab === 'new' && !trainingEpisodes.length && Boolean(weightSet);
}

function buildZeroThetaWeights(): number[] | null {
  if (!weightSet) {
    return null;
  }
  const length = weightSet.rows * weightSet.cols;
  return Array.from({ length }, () => 0);
}

function buildZeroActionSeries(): number[][] {
  const rows = weightSet?.rows ?? ACTUATOR_NAMES.length;
  return Array.from({ length: rows }, () => Array(SNAPSHOT_WINDOW_LENGTH).fill(0));
}

function buildZeroRewardSeries(): number[] | null {
  return trainingEpisodes.length ? Array(trainingEpisodes.length).fill(0) : (weightSet ? [0] : null);
}

function getEpisodeRewardValue(episode: EpisodeResult): number | null {
  if (typeof episode.totalReturn === 'number' && Number.isFinite(episode.totalReturn)) {
    return episode.totalReturn;
  }
  if (typeof episode.reward === 'string') {
    const parsed = Number(episode.reward);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function buildTrainingRewardSeries(): number[] | null {
  if (!trainingEpisodes.length) {
    return null;
  }
  const series: number[] = [];
  let hasFinite = false;
  let lastValue = 0;
  trainingEpisodes.forEach((episode) => {
    const value = getEpisodeRewardValue(episode);
    if (typeof value === 'number' && Number.isFinite(value)) {
      lastValue = value;
      hasFinite = true;
      series.push(value);
    } else if (hasFinite) {
      series.push(lastValue);
    } else {
      series.push(0);
    }
  });
  return hasFinite ? series : null;
}

function updateTrainingRewardPlot(): void {
  if (!trainingView) {
    return;
  }
  const series = buildTrainingRewardSeries();
  trainingView.setRewardSeries(series);
}

function createEpisodeWeightSnapshot(episode: EpisodeResult): TrainingWeightRunEpisode {
  return {
    episode: episode.episode,
    total_episodes: episode.totalEpisodes,
    theta_weights: Array.isArray(episode.thetaWeights) ? [...episode.thetaWeights] : [],
    reward: episode.reward ?? null,
    total_return: episode.totalReturn ?? null,
  };
}

function buildTrainingWeightExportPayload(episode: EpisodeResult): TrainingWeightExportPayload {
  const snapshot = createEpisodeWeightSnapshot(episode);
  const parameters = lastRunTrainingParameters ? { ...lastRunTrainingParameters } : null;
  return {
    exported_at: new Date().toISOString(),
    episode: snapshot.episode,
    total_episodes: snapshot.total_episodes,
    theta_weights: snapshot.theta_weights,
    reward: snapshot.reward ?? null,
    total_return: snapshot.total_return ?? null,
    parameters,
  };
}

function buildWeightExportFilename(episode: EpisodeResult): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const label = Number.isFinite(episode.episode) ? `ep-${episode.episode}` : 'episode';
  return `training-weights-${label}-${timestamp}.json`;
}

function buildTrainingHistoryEpisodes(timestamp: string): TrainingHistoryEpisode[] {
  return trainingEpisodes.map((episode) => ({
    episode: episode.episode,
    total_episodes: episode.totalEpisodes,
    theta_weights: Array.isArray(episode.thetaWeights) ? [...episode.thetaWeights] : [],
    reward: episode.reward ?? null,
    total_return: episode.totalReturn ?? null,
    recorded_at: timestamp,
  }));
}

function buildTrainingHistoryMetadata(basisWindow: number[][]): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  if (lastRunTrainingParameters?.reward) {
    metadata.reward_mode = lastRunTrainingParameters.reward;
  }
  if (lastRunTrainingParameters?.jointRangeMin !== undefined || lastRunTrainingParameters?.jointRangeMax !== undefined) {
    metadata.joint_range = {
      min: Number(lastRunTrainingParameters?.jointRangeMin ?? 0),
      max: Number(lastRunTrainingParameters?.jointRangeMax ?? 0),
    };
  }
  if (weightSet) {
    metadata.weight_shape = { rows: weightSet.rows, cols: weightSet.cols };
  } else if (trainingEpisodes.length && Array.isArray(trainingEpisodes[0]?.thetaWeights)) {
    const first = trainingEpisodes[0]?.thetaWeights?.length ?? 0;
    metadata.weight_shape = { rows: ACTUATOR_NAMES.length, cols: Math.max(1, Math.floor(first / Math.max(ACTUATOR_NAMES.length, 1))) };
  }
  if (basisWindow.length) {
    metadata.basis_snapshots = basisWindow;
  }
  return metadata;
}

function buildTrainingHistorySummary(): TrainingHistorySummary {
  const episodesRecorded = trainingEpisodes.length;
  let bestReturn: number | null = null;
  let sumReturn = 0;
  let counted = 0;
  let finalReturn: number | null = null;
  trainingEpisodes.forEach((episode) => {
    const value = typeof episode.totalReturn === 'number' ? episode.totalReturn : Number.NaN;
    if (Number.isFinite(value)) {
      bestReturn = bestReturn === null ? value : Math.max(bestReturn, value);
      sumReturn += value;
      counted += 1;
      finalReturn = value;
    }
  });
  const averageReturn = counted > 0 ? sumReturn / counted : null;
  return {
    episodes_recorded: episodesRecorded,
    best_return: bestReturn,
    average_return: averageReturn,
    final_return: finalReturn,
  };
}

function buildTrainingHistoryExportPayload(
  metadata: ActiveTrainingRunMetadata | null,
  completionTimestamp?: string,
): TrainingHistoryExportPayload | null {
  if (!hasEpisodeWeightData()) {
    return null;
  }
  const completedAt = completionTimestamp ?? new Date().toISOString();
  const startedAt = metadata?.startedAt ?? (trainingStartTimestamp ? new Date(trainingStartTimestamp).toISOString() : completedAt);
  const durationMs = Math.max(0, Date.parse(completedAt) - Date.parse(startedAt));
  const jobId = metadata?.id ?? `local-${completedAt}`;
  const parameters = metadata?.parameters
    ? { ...metadata.parameters }
    : lastRunTrainingParameters
      ? { ...lastRunTrainingParameters }
      : null;
  const labelFromParams = buildTrainingRunLabelFromParameters(parameters);
  const label = metadata?.label ?? labelFromParams ?? buildTrainingRunLabel(Date.parse(completedAt));
  const basisWindow = captureBasisSnapshotWindow();
  return {
    format_version: TRAINING_HISTORY_FORMAT_VERSION,
    job: {
      id: jobId,
      label,
      started_at: startedAt,
      completed_at: completedAt,
      duration_ms: durationMs,
      algo: 'agol',
      env_xml: null,
    },
    parameters,
    metadata: buildTrainingHistoryMetadata(basisWindow),
    summary: buildTrainingHistorySummary(),
    episodes: buildTrainingHistoryEpisodes(completedAt),
  };
}

function buildTrainingHistoryFilename(payload: TrainingHistoryExportPayload): string {
  const timestamp = (payload.job.completed_at || payload.job.started_at).replace(/[:.]/g, '-');
  const slug = payload.job.label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || payload.job.id;
  return `training-history-${slug}-${timestamp}.json`;
}

function getLatestTrainingHistoryExportPayload(): TrainingHistoryExportPayload | null {
  if (isViewingHistory) {
    const run = getSelectedTrainingRun();
    if (run?.history) {
      return cloneTrainingHistoryPayload(run.history);
    }
  }
  if (lastCompletedHistoryPayload) {
    return cloneTrainingHistoryPayload(lastCompletedHistoryPayload);
  }
  return buildTrainingHistoryExportPayload(activeTrainingRun, undefined);
}

function cloneTrainingHistoryPayload(
  source: TrainingHistoryExportPayload,
): TrainingHistoryExportPayload {
  return {
    format_version: source.format_version,
    job: { ...source.job },
    parameters: source.parameters ? { ...source.parameters } : null,
    metadata: { ...source.metadata },
    summary: { ...source.summary },
    episodes: source.episodes.map((episode) => ({
      ...episode,
      theta_weights: Array.isArray(episode.theta_weights) ? [...episode.theta_weights] : [],
    })),
  };
}

function downloadJsonFile(
  payload: TrainingWeightExportPayload | TrainingWeightRunExportPayload | TrainingHistoryExportPayload,
  filename: string,
): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body?.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function handleEpisodeInputCommit(): void {
  if (!episodeInput) {
    return;
  }
  if (!trainingEpisodes.length) {
    updateEpisodeNav();
    return;
  }
  const parsed = Number(episodeInput.value);
  if (!Number.isFinite(parsed)) {
    updateEpisodeNav();
    return;
  }
  const desiredIndex = Math.round(parsed) - 1;
  showEpisode(desiredIndex);
  updateEpisodeNav();
}

function resetTrainingResults(): void {
  stopTrainingPolling();
  stopSpeedrun();
  trainingEpisodes = [];
  currentEpisodeIndex = -1;
  trainingActionMode = 'theta';
  trainingView?.setEpisodeSeries(null);
  trainingView?.setRewardSeries(null);
  trainingView?.setRewardSeriesLength(null);
  renderTrainingWeights(null);
  updateEpisodeNav();
  updateTrainingWeightButtons();
  syncActionPlotRange();
}

function setTrainingStatusText(message: string, showElapsed?: boolean): void {
  trainingStatusMessage && (trainingStatusMessage.textContent = message);
  if (typeof showElapsed === 'boolean' && trainingElapsedNode) {
    trainingElapsedNode.hidden = !showElapsed;
  }
}

function updateTrainingElapsedLabel(): void {
  if (!trainingElapsedNode || trainingStartTimestamp <= 0) {
    return;
  }
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - trainingStartTimestamp) / 1000));
  trainingElapsedNode.textContent = `Elapsed: ${elapsedSeconds}s`;
}

function startTrainingElapsedTimer(): void {
  if (!trainingElapsedNode) {
    return;
  }
  const update = (): void => {
    updateTrainingElapsedLabel();
  };
  update();
  stopTrainingElapsedTimer(false);
  trainingElapsedHandle = window.setInterval(update, 1000);
  trainingElapsedNode.hidden = false;
}

function stopTrainingElapsedTimer(hideLabel: boolean): void {
  if (trainingElapsedHandle) {
    window.clearInterval(trainingElapsedHandle);
    trainingElapsedHandle = null;
  }
  if (!hideLabel) {
    updateTrainingElapsedLabel();
  } else if (trainingElapsedNode) {
    trainingElapsedNode.hidden = true;
  }
}

function stopTrainingPolling(): void {
  if (trainingPollHandle) {
    window.clearTimeout(trainingPollHandle);
    trainingPollHandle = null;
  }
}

function updateEpisodeNav(): void {
  const hasEpisodes = trainingEpisodes.length > 0;
  if (episodeNav) {
    episodeNav.hidden = !hasEpisodes;
  }
  updateSpeedrunButtonState();
  updateTrainingActionToggle();
  if (!hasEpisodes) {
    episodePrevButton && (episodePrevButton.disabled = true);
    episodeNextButton && (episodeNextButton.disabled = true);
    if (episodeInput) {
      episodeInput.value = '0';
      episodeInput.disabled = true;
      episodeInput.removeAttribute('max');
    }
    episodeTotalLabel && (episodeTotalLabel.textContent = '0');
    return;
  }
  const hasMultipleEpisodes = trainingEpisodes.length > 1;
  episodePrevButton && (episodePrevButton.disabled = !hasMultipleEpisodes);
  episodeNextButton && (episodeNextButton.disabled = !hasMultipleEpisodes);
  if (episodeInput) {
    const safeIndex = currentEpisodeIndex >= 0 ? currentEpisodeIndex + 1 : 1;
    episodeInput.disabled = false;
    episodeInput.max = `${trainingEpisodes.length}`;
    episodeInput.value = `${Math.min(Math.max(safeIndex, 1), trainingEpisodes.length)}`;
  }
  episodeTotalLabel && (episodeTotalLabel.textContent = `${trainingEpisodes.length}`);
}

function getWrappedEpisodeIndex(delta: number): number | null {
  if (!trainingEpisodes.length) {
    return null;
  }
  const count = trainingEpisodes.length;
  if (currentEpisodeIndex < 0) {
    return delta >= 0 ? 0 : count - 1;
  }
  const rawIndex = (currentEpisodeIndex + delta) % count;
  return rawIndex >= 0 ? rawIndex : rawIndex + count;
}

function showEpisode(index: number): void {
  if (!trainingEpisodes.length) {
    return;
  }
  const nextIndex = Math.min(Math.max(index, 0), trainingEpisodes.length - 1);
  if (nextIndex === currentEpisodeIndex) {
    return;
  }
  currentEpisodeIndex = nextIndex;
  renderCurrentEpisode();
}

function normaliseActionMatrix(
  source: (number | null)[][] | null | undefined,
): number[][] | null {
  if (!Array.isArray(source) || source.length === 0) {
    return null;
  }
  const outputs = ACTUATOR_NAMES.length;
  let hasSamples = false;
  const matrix: number[][] = [];
  for (let i = 0; i < outputs; i += 1) {
    const rowSource = source[i] ?? [];
    const rowHasSamples = rowSource.some((value) => typeof value === 'number');
    if (rowHasSamples) {
      hasSamples = true;
    }
    matrix.push(rowSource.map((value) => (typeof value === 'number' ? value : 0)));
  }
  return hasSamples ? matrix : null;
}

function createEmptyActionMatrix(): number[][] {
  return Array.from({ length: ACTUATOR_NAMES.length }, () => []);
}

function normaliseThetaWeights(source: number[] | null | undefined): number[] | null {
  if (!Array.isArray(source)) {
    return null;
  }
  const values: number[] = [];
  source.forEach((value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      values.push(value);
    }
  });
  return values.length ? values : null;
}

function filterEpisodeListByIndex<T extends { episode?: number; total_episodes?: number }>(
  entries: T[] | null | undefined,
): T[] {
  if (!Array.isArray(entries)) {
    return [];
  }
  const seenEpisodes = new Set<number>();
  return entries.filter((entry) => {
    const episodeNumber = Number(entry?.episode);
    if (!Number.isFinite(episodeNumber)) {
      return false;
    }
    const totalEpisodes = Number(entry?.total_episodes);
    if (Number.isFinite(totalEpisodes) && episodeNumber > totalEpisodes) {
      return false;
    }
    if (seenEpisodes.has(episodeNumber)) {
      return false;
    }
    seenEpisodes.add(episodeNumber);
    return true;
  });
}

function normaliseEpisode(dto: EpisodeProgressDto): EpisodeResult {
  const actions = normaliseActionMatrix(dto.actions) ?? createEmptyActionMatrix();
  const thetaActions = normaliseActionMatrix(dto.theta_actions);
  const thetaWeights = normaliseThetaWeights(dto.theta_weights);
  return {
    episode: dto.episode,
    totalEpisodes: dto.total_episodes,
    actions,
    thetaActions,
    thetaWeights,
    reward: dto.reward,
    totalReturn: dto.total_return,
  };
}

function renderCurrentEpisode(): void {
  if (isZeroWeightsSelectionActive) {
    trainingView?.setEpisodeSeries(buildZeroActionSeries());
    trainingView?.setRewardSeries(buildZeroRewardSeries());
    trainingView?.setRewardSeriesLength(null);
    renderTrainingWeights(buildZeroThetaWeights());
    updateEpisodeNav();
    updateTrainingWeightButtons();
    return;
  }
  if (currentEpisodeIndex < 0 || !trainingEpisodes.length) {
    if (shouldShowZeroWeightsPlaceholder()) {
      trainingView?.setEpisodeSeries(buildZeroActionSeries());
      trainingView?.setRewardSeries(buildZeroRewardSeries());
      trainingView?.setRewardSeriesLength(null);
      renderTrainingWeights(buildZeroThetaWeights());
    } else {
      trainingView?.setEpisodeSeries(null);
      trainingView?.setRewardSeries(null);
      trainingView?.setRewardSeriesLength(null);
      renderTrainingWeights(null);
    }
    updateEpisodeNav();
    updateTrainingWeightButtons();
    return;
  }
  const current = trainingEpisodes[currentEpisodeIndex];
  trainingView?.setEpisodeSeries(getEpisodeSeriesForMode(current));
  updateTrainingRewardPlot();
  trainingView?.setRewardSeriesLength(currentEpisodeIndex);
  renderTrainingWeights(current.thetaWeights);
  updateEpisodeNav();
  updateTrainingWeightButtons();
}

async function handleStartTraining(): Promise<void> {
  if (!trainingView || !trainingStartButton || activeTrainingJobId) {
    return;
  }
  clearHistoryView({ suppressRender: true });
  const values = trainingView.getParameters();
  trainingParameters = values;
  lastRunTrainingParameters = { ...values };
  resetTrainingResults();
  trainingStartButton.disabled = true;
  setTrainingStatusText('Training queued...', false);
  try {
    const payload = buildTrainingPayload(values);
    const response = await apiFetch('/training/schedule', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const data = (await response.json()) as { job_id: string };
    activeTrainingJobId = data.job_id;
    renderCurrentEpisode();
    syncActionPlotRange();
    const startedAt = new Date().toISOString();
    const labelFromParameters = buildTrainingRunLabelFromParameters(lastRunTrainingParameters);
    activeTrainingRun = {
      id: data.job_id,
      label: labelFromParameters ?? buildTrainingRunLabel(Date.now()),
      startedAt,
      parameters: lastRunTrainingParameters ? { ...lastRunTrainingParameters } : null,
    };
    lastCompletedHistoryPayload = null;
    trainingStartTimestamp = Date.now();
    startTrainingElapsedTimer();
    setTrainingStatusText('Training running...', true);
    void pollTrainingProgress(data.job_id);
  } catch (error) {
    console.error(error);
    setTrainingStatusText('Training failed', false);
    trainingStartButton.disabled = false;
    stopTrainingElapsedTimer(true);
    activeTrainingRun = null;
    refreshTrainingHistoryUI();
  }
}

async function pollTrainingProgress(jobId: string): Promise<void> {
  try {
    const response = await apiFetch(`/training/progress/${encodeURIComponent(jobId)}`);
    if (!response.ok) {
      throw new Error('progress fetch failed');
    }
    const payload = (await response.json()) as EpisodeProgressDto;
    if (Array.isArray(payload.history) && payload.history.length) {
      finalizeTraining(payload.history);
      return;
    }
  } catch (error) {
    console.warn('Training progress check failed', error);
  }
  trainingPollHandle = window.setTimeout(() => {
    void pollTrainingProgress(jobId);
  }, 3000);
}

function finalizeTraining(history: EpisodeProgressDto[]): void {
  trainingStartButton && (trainingStartButton.disabled = false);
  stopTrainingElapsedTimer(false);
  stopTrainingPolling();
  activeTrainingJobId = null;
  const filteredHistory = filterEpisodeListByIndex(history);
  trainingEpisodes = filteredHistory.map(normaliseEpisode);
  currentEpisodeIndex = trainingEpisodes.length ? trainingEpisodes.length - 1 : -1;
  syncActionPlotRange();
  const completedAt = new Date().toISOString();
  const payload = buildTrainingHistoryExportPayload(activeTrainingRun, completedAt);
  activeTrainingRun = null;
  if (payload) {
    recordCompletedTrainingRun(payload);
  }
  if (trainingEpisodes.length) {
    renderCurrentEpisode();
    setTrainingStatusText('Training complete', true);
  } else {
    resetTrainingResults();
    setTrainingStatusText('Training complete (no data)', true);
  }
}
window.addEventListener('DOMContentLoaded', () => {
  setupTabs();
  setupTrainingControls();
  trainingHistoryLoadPromise = loadTrainingHistoryFromFolder();
  void trainingHistoryLoadPromise;
  void bootstrap();
});




